import { CRIT_CASCADE_RULES } from '../../actions/weapon-attack-cascades.js';
import { ignoresDamageTypeResistance } from './resistance-ignore.js';
import { applyDamagePercentage } from './damage-percentage.js';
import { nextSpellDamageDice, nextSpellPlan } from '../../sanguine/next-spell.js';
import { queueNextSpellTarget } from '../next-spell.js';
import { resolveSelfZeroTransform } from '../../spawn/zero-vitality';
import { composeActualDeath, deathResourcesFor, dropDyingHands } from '../../death/compose';
import { deathLanding } from '../../death/grounding';
import { FALLING_DAMAGE_ID, resolveFallDamage } from '../movement/fall-damage';
import { grantControllerMenace } from '../../death/controller-menace';
import { resolveDyingStacks } from '../../death/dying';
import { tryLastStand, actualDeathTriggers } from '../../death/self-zero';
import { skipDeathDuty } from '../../death/skip';
import { refuseDeath } from '../../death/refusal';
import { isAnimatedSummon, summonControllerId, triggerSummonDeath } from '../../death/summons';
import { consentPending, TRIGGER_CONSENT_REASON } from '../trigger-consent';
import { resolveConnectionCascade } from '../../statuses/gm-boundary.js';
import { saveAbilityObjectDamage, InventoryDamageFactsNeeded } from '../../inventory/ability-damage.js';
import { resolveObjectVitality, installObjectVitality } from '../../inventory/object-vitality.js';
import { wallRunDamageRedirect } from '../../inventory/wall-run-damage.js';
import { damageTypePickOf } from '../damage-type-pick.js';
import { heldStatusItemId } from '../statuses/target-status-gate.js';
import { type CritHouseRule, type JanusCtx, type Row, type RollFeedback, activeStatuses, ec, ecId, ecLean, handBack, insertRoll, int, inventoryId, isInventoryRow, itemField, pendingStatusDefinitions, record, requiredInt, scalar, upper, definitionNotHeld } from '../context.js';
import { carrierAuthorsAny, carrierDefinition } from '../statuses/status-trigger-gate.js';
import { forcedEntryId } from '../statuses/forced-roll-extreme.js';
import { abilityEntryId } from '../charges/charge-entry.js';
import { damageHealsCaster } from './healing-roll.js';
import { type JanusInput } from '../janus.js';
import { type TriggerCandidate, ZERO_VITALITY_WITNESS, circlePredicateMatch, zeroVitalityWitnessFact } from '../../reactions/trigger-dispatch.js';
import { spellCircles } from '../spell-circle-scope.js';
import { janusCtxTriggerReaders } from '../../reactions/janus-ctx-trigger-readers.js';
import { RANGE_SELF, TAKES_ANY_DAMAGE, TAKES_DAMAGE_TYPE, TARGET_TAKES_ANY_DAMAGE, TARGET_TAKES_DAMAGE_TYPE, triggerExecutorGap,
  triggerFamilyPreDraw, runTriggerFamily, onKillGap } from '../trigger-executor.js';
import { checkScalableCantrip } from '../check-scalable-cantrip.js';
import { rollDiceFaces, type DieTuple } from './roll-dice-arr.js';
import { rollTypeSourceStacks } from './roll-type-roll-shared.js';
import { isVariableAmount, variableDice } from '../variable-amount.js';
import { discernVariableTags } from '../discern-variable-tags.js';
import { phpIntval } from '../../rolls/dice-roll.js';
import { applyStatusEffect, QUANTITY_FAMILIES } from '../statuses/apply-status-effect.js';
import { applyStatusSkillGlobals } from '../../compile/creature-status-skill-globals.js';
import { hydrateCreatureSkillEntry } from '../../compile/creature-skill-entry-hydration.js';
import { readStatusRowStack } from '../../compile/status-stack-reader.js';
import { statusScalar } from '../../actions/activity.js';
import { combatCenter } from '../../actions/ability-preconditions.js';
import { MITIGATION_LAYERS, defenseMeta, matchDefenseEntry } from '../../actions/weapon-attack.js';
import { damageReductionPlan, rollDamageReductions, type DamageReduction } from './reduce-damage-status.js';
import { selectSkillForContestingRoll } from '../select-skill-for-contesting-roll.js';
import { skillRollArr } from '../skill-roll.js';
import { criticalHitBonus, criticalHitDamageMultiplier } from '../../actions/weapon-attack-dice.js';
import { upcastRoll } from '../upcast.js';
import { surfaceDamageImpact } from '../surface-reactions.js';
import { planWake, performWake, SLEEPING } from '../statuses/wake-on-damage.js';
import { planContestRetry, performContestRetry, planCharmBreak, performCharmBreak } from '../statuses/retry-contest-on-damage.js';
import { EXTRA_DAMAGE_REQUIRES_DAMAGE_TYPE, extraDamageTypeGateAllows } from './extra-damage-requires-damage-type.js';
import { passiveEntryActive, statusItemPassiveEntries } from './status-item-passive-entries.js';
import { ctxDamageProviders, damageProviderFlag, ignoredMitigationTypes, mitigationAfterTypedIgnore } from './ignore-damage-mitigation-damage-type.js';
import { ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION, SPELL_ATTACK_ROLL_TYPES, mitigationAfterNaturalIgnore, naturalDamageMitigation } from './ignore-natural-damage-mitigation.js';
import { ctxDamageTypesRolledAtMax, ctxProviderReaders } from '../caster-spell-layers.js';
import { casterLayerProvider } from '../../actions/cast-range-gate.js';
import { scrollSpellDamageMultiplier } from '../charged-cast.js';
import { runK4OnHit } from '../k4-dispatch.js';
import { statusItemId } from '../../statuses/expiry.js';
import { DEAD_STATUS_ID } from '../../death/state-ids';
import { patchVitalityCluster } from '../../compile/held-vitality';
import { consumeShielded } from '../statuses/consume-shielded.js';
import { deadDamageTargetSkips } from '../is-target-valid-for-effect.js';
import { removeStoredStatus } from '../statuses/status-effect-removal-helpers.js';
import { getStatusEffectDurationTimes } from '../statuses/get-status-effect-duration-times';
import { damageStatusScope } from '../../actions/damage-status-scope.js';
import { absorbTemporaryVitality, requireTemporaryVitalityLane } from './temporary-vitality.js';
import { HIDDEN, SNEAKING } from '../stealth-hide.js';
import { CONTINGENCY_ARMING_IDS } from '../../sanguine/sanguine-gate.js';
import { fireDamageContingencies } from '../../sanguine/contingency-fire.js';
import { phpJsonTruthy } from '../../compile/php-json-values.js';
import { isAuraPassive } from '../../compile/passive-integer-fold.js';
import { createAbilityConditionEvaluator, isAbilityConditionLayer } from '../../compile/ability-conditions.js';
import { answerConditionContext, ConditionContextUnknown } from '../../compile/condition-context.js';
import { C1_CONTRACT } from '../../compile/possessor-compile.js';
import { findInventoryRow } from '../../inventory/flag-adjust.js';

const DAMAGE_ROLL = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';
// StatusEffect 'concentrating', DefensiveSkill 'psyche', RollType 'dc_roll' (additions.php).
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
const PSYCHE = '58A62523-2588-452B-876B-61D8ACF3CCED';
const DC_ROLL = 'ED5B44EA-7438-9582-A388-04EFF342E460';
// RollType 'damage_over_time_roll' (the damage row type of a DoT / contested zone tick).
const DOT_ROLL = '77A044B7-7041-CA96-3C63-BB718B15A364';
const EXTRA_DAMAGE_ROLL = '9878714A-27C3-D737-88B3-E8C1EE63A20F';

function rollTypeIds(ctx: JanusCtx, row: Row | null, key: string): string[] {
  // PHP $list_contains normalisation (forcedEntryId: wrapper [0], item_layer_effect / items.item_id / item_id cut at '|', bare string whole);
  // a nested item_arr shell is read by its own item id.
  return ctx.read.entries(row, key).map((entry) => forcedEntryId(entry)
    || upper(itemField(record(record(entry)?.item_arr), 'item_id'))).filter(Boolean);
}
function rollTypeNames(ctx: JanusCtx, row: Row | null, key: string, rollType: string): boolean {
  return rollTypeIds(ctx, row, key).includes(rollType);
}
function rollTypeStatusDefinition(ctx: JanusCtx, status: Row, role: string): Row {
  const inline = record(status.status_arr);
  // A collapsed status may carry only {items:{item_id}}. That shell cannot prove the absence of a modifier.
  const definition = inline && Object.keys(inline).some((key) => key !== 'items')
    ? inline : ctx.read.definition(upper(status.status_item_id));
  if (!definition) handBack(`damage_type_roll:${role}.status-definition-not-held`);
  return definition;
}
function rollTypeStatusName(status: Row, definition: Row): string {
  return String(itemField(status, 'item_name') ?? itemField(definition, 'item_name') ?? 'Status Effect');
}
function rollTypeFragmentModifiers(ctx: JanusCtx, caster: Row, target: Row, rollType: string): {
  extreme: 'max' | 'min' | null; casterExtreme: 'max' | 'min' | null;
  casterHalve: string | null; targetDouble: string | null; targetHalve: string | null;
} {
  let max = rollTypeNames(ctx, caster, 'roll_type_is_rolled_at_max', rollType);
  let min = rollTypeNames(ctx, caster, 'roll_type_is_rolled_at_min', rollType);
  let casterHalve: string | null = null;
  for (const status of activeStatuses(ctx, caster)) {
    const def = rollTypeStatusDefinition(ctx, status, 'caster');
    max ||= rollTypeNames(ctx, def, 'roll_type_is_rolled_at_max', rollType);
    min ||= rollTypeNames(ctx, def, 'roll_type_is_rolled_at_min', rollType);
    if (casterHalve === null && rollTypeNames(ctx, def, 'roll_type_roll_result_is_halved', rollType))
      casterHalve = rollTypeStatusName(status, def);
  }
  if (casterHalve === null && rollTypeNames(ctx, caster, 'roll_type_roll_result_is_halved', rollType)) casterHalve = 'Status Effect';
  const casterExtreme = max ? 'max' : min ? 'min' : null;
  let targetDouble: string | null = null, targetHalve: string | null = null;
  for (const status of activeStatuses(ctx, target)) {
    const def = rollTypeStatusDefinition(ctx, status, 'target');
    if (ctx.read.has(def, 'target_marked_by_caster')
        && upper(status.status_caster_id ?? status.caster ?? status.status_target_id) !== ecId(caster)) continue;
    max ||= rollTypeNames(ctx, def, 'roll_type_against_is_rolled_at_max', rollType);
    min ||= rollTypeNames(ctx, def, 'roll_type_against_is_rolled_at_min', rollType);
    if (targetDouble === null && rollTypeNames(ctx, def, 'roll_type_against_roll_result_is_doubled', rollType))
      targetDouble = rollTypeStatusName(status, def);
    if (targetHalve === null && rollTypeNames(ctx, def, 'roll_type_against_roll_result_is_halved', rollType))
      targetHalve = rollTypeStatusName(status, def);
  }
  return { extreme: max ? 'max' : min ? 'min' : null, casterExtreme, casterHalve, targetDouble, targetHalve };
}
function hasTriggerKey(row: Row): boolean {
  return [...Object.keys(row), ...Object.keys(record(row.item_layer_types) ?? {})].some((key) => key.includes('trigger'));
}
function casterDoubleSource(ctx: JanusCtx, caster: Row, target: Row, ability: Row, rollType: string): {
  standing: string | null; active: string | null;
} {
  const targetType = String(itemField(target, 'item_type') ?? record(target.encounter_creatures)?.item_type ?? '');
  let standing: string | null = null;
  for (const key of ['passive_ability', 'feat']) {
    for (const entry of ctx.read.entries(caster, key)) {
      const row = record(entry);
      if (!row) continue;
      if (key === 'passive_ability' && row.passive_conditions_active === false) continue;
      const def = record(row.item_arr) ?? ctx.read.definition(upper(row.item_id ?? itemField(row, 'item_id')));
      if (!def) definitionNotHeld('damage_type_roll:caster-double-source-definition-not-held', upper(row.item_id ?? itemField(row, 'item_id')));
      if (hasTriggerKey(def) || !rollTypeNames(ctx, def, 'roll_type_roll_result_is_doubled', rollType)) continue;
      if (ctx.read.has(def, 'ability_works_only_against_item_type')
          && !ctx.read.entries(def, 'ability_works_only_against_item_type').map(String).includes(targetType)) continue;
      standing = String(itemField(def, 'item_name') ?? 'Ability');
      break;
    }
    if (standing !== null) break;
  }
  const attackRider = record(ability.attack_ability_arr);
  const activeAbility = attackRider ?? ability;
  const activeType = String(itemField(activeAbility, 'item_type') ?? '');
  const activeTypes = new Set(['full_ability', 'ability', 'quick_ability', 'lair_ability', 'trap_ability',
    'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta',
    'sanguine', 'spell', 'potion', 'coating', 'food', 'drink', 'elixir']);
  let active: string | null = null;
  if (activeTypes.has(activeType) && !hasTriggerKey(activeAbility)
      && rollTypeNames(ctx, activeAbility, 'roll_type_roll_result_is_doubled', rollType)) {
    const held = new Set(activeStatuses(ctx, target).map((status) => upper(status.status_item_id)));
    const vStatus = upper(record(record(target.computed)?.vitality)?.v_status);
    if (vStatus) held.add(vStatus);
    const required = rollTypeIds(ctx, activeAbility, 'if_target_has_status_effect_active');
    const forbidden = rollTypeIds(ctx, activeAbility, 'if_target_has_status_effect_not_active');
    if ((!ctx.read.has(activeAbility, 'if_target_has_status_effect_active') || required.some((id) => held.has(id)))
        && !forbidden.some((id) => held.has(id))) active = String(itemField(activeAbility, 'item_name') ?? 'Ability');
  }
  return { standing, active };
}

/** Highest eligible provider wins; sources never multiply together. Resolved pre-draw and reused for every fragment. */
export function objectDamageMultiplier(ctx: JanusCtx, caster: Row, ability: Row,
  providers: readonly Row[] = ctxDamageProviders(ctx, caster, ability, new Set(['damage_to_objects_multiplier']))): { factor: number; source: string } {
  let factor = 1, source = '';
  // Retain the existing first-source tie order: caster, attack rider, used item, then other eligible providers.
  for (const provider of [caster, record(ability.attack_ability_arr), ability, ...providers]) {
    if (!provider) continue;
    for (const row of [provider, { ...provider, item_layer_types: undefined }]) {
      const n = Math.trunc(Number(scalar(ctx.read.value(row, 'damage_to_objects_multiplier'))));
      if (Number.isFinite(n) && n > factor) { factor = n; source = String(itemField(provider, 'item_name') ?? '') || 'Ability'; }
    }
  }
  return { factor, source };
}

/** concentration_roll_is_rolled_at_max / _at_min on the CONCENTRATING creature (2026-10-07; PHP legacy copy
 * ft_concentration_roll_forced_natural — R-145): 20 / 1 / null. At max = a passive promoted to the ROOT by finalize_creature_arr
 * (Psion Hallucinarch "Mind Against Matter"); at min = an enemy aura's status folded onto the occupant ROOT through
 * DynamicLayer::LIST (Telepath "Mental Interference"). Both held cancel to a normal roll (like one ADV + one DIS stack). */
export function concentrationForcedNatural(ctx: Pick<JanusCtx, 'read'>, target: Row): 1 | 20 | null {
  // PHP !empty(layer_value()): the reader's value decides ('0' is off). Only when the bucket-first reader yields nothing (an empty
  // item_layer_types slot stops it before the root) is the promoted / status-folded ROOT read directly (Codex review 2026-10-07).
  const on = (raw: unknown): boolean => { const v = Array.isArray(raw) ? raw[0] : raw; return v != null && v !== '' && v !== 0 && v !== '0' && v !== false; };
  const held = (key: string): boolean => { const v = ctx.read.value(target, key); return v != null ? on(v) : on(target[key]); };
  const max = held('concentration_roll_is_rolled_at_max'), min = held('concentration_roll_is_rolled_at_min');
  return max === min ? null : max ? 20 : 1;
}
/** check_concentration.php: the target's Psyche DC save against max(10, floor(damage / 2)), ADV / DIS from its
 * contesting_roll_{adv,dis}_vs_status_effect entries naming Concentrating (one stack each). Maintained → its log row;
 * broken → the shared browser connection cascade and one concentration_broken row. */
export function concentrationCheck(ctx: JanusCtx, input: JanusInput, target: Row, status: Row, damage: number, maimedDelta = 0): void {
  // An earlier failed save in this same use already retired the row.
  if (ctx.statusesRemoved.some(s => upper(s.status_id) === upper(statusScalar(status, 'status_id')))) return;
  // Damage applies critical and dying Maimed before concentration. The weapon transport persists those
  // decided stacks separately; the save view includes their penalties without writing them twice.
  const computed = record(target.computed), creature = record(computed?.creature), skills = record(creature?.skills);
  let saveSkills = skills;
  if (maimedDelta !== 0) {
    const definition = ctx.read.definition(MAIMED_ID);
    if (!definition) definitionNotHeld('check_concentration:maimed-definition-not-held', MAIMED_ID);
    const delta = applyStatusSkillGlobals({ skills: skills ?? undefined, modifiers: record(creature?.modifiers) ?? undefined,
      modifierSources: record(creature?.modifier_sources) ?? undefined, layers: record(definition.item_layer_types) ?? {},
      statusName: String(itemField(definition, 'item_name') ?? ''), statusRowId: '', applied: maimedDelta > 0, stacks: Math.abs(maimedDelta),
      skillEntries: [{ variable_name: 'psyche', item_type: 'defensive_skill' }],
      heldStatusIds: new Set(ctx.read.statuses(target).map(row => upper(statusScalar(row, 'status_id')))) });
    if (!delta.ok) handBack('check_concentration:' + delta.reason);
    saveSkills = delta.skills;
  }
  const psyche = record(saveSkills?.psyche);
  if (psyche) {
    const stored = { ...psyche };
    if (maimedDelta !== 0) delete stored.mod;
    target = { ...target, computed: { ...computed, creature: { ...creature, skills: { ...saveSkills,
      psyche: hydrateCreatureSkillEntry(stored, 'psyche', { byVariable: {}, byId: {} }) } } } };
  }
  const skill = selectSkillForContestingRoll(ctx, target, [PSYCHE]);
  // The shared reader includes computed defenses and deduplicates their provider UUIDs.
  // Each matching family supplies one stack, even when several sources grant it.
  for (const [key, sign] of [['contesting_roll_adv_vs_status_effect', 1], ['contesting_roll_dis_vs_status_effect', -1]] as const) {
    if (ctx.read.entries(target, key).some(entry => upper(listEntryUuid(entry)) === CONCENTRATING)) skill.adv += sign;
  }
  const dc = Math.max(10, Math.floor(damage / 2));
  // A SELF-damaging use saves on the ONE live row (FRONT-END RULE, Session A step 4, 2026-09-30 — the counterspell-watcher-luck class):
  // PHP saves on its own $target_arr copy of the caster, which never saw what the caster copy changed this use (a natural-1 contest's
  // luck drop — AO-231-rich rolled the save plain; the browser rolls it unlucky, as the rule says). The census reports that divergence
  // as FRONT-END RULED (self-save-luck), never as exact.
  const result = skillRollArr(ctx, input, target, skill, null, dc, { rollType: DC_ROLL, asTarget: true, isTarget: false,
    forcedNatural: concentrationForcedNatural(ctx, target) });
  if (result < dc) {
    resolveConnectionCascade(ctx, status, ecId(target));
    insertRoll(ctx, { roll_type_1: 'concentration_broken', roll_item_id: CONCENTRATING, by: target });
  }
  else insertRoll(ctx, { roll_type_1: 'concentration_maintained', by: target });
}
export function damageMaimedStacks(ctx: JanusCtx, target: Row): number {
  const row = activeStatuses(ctx, target).find(status => statusItemId(status) === MAIMED_ID);
  if (!row) return 0;
  return ctx.statusStacks?.find(change => upper(change.status_id) === upper(statusScalar(row, 'status_id')))?.status_stack
    ?? readStatusRowStack(row);
}
/** Damage reactions precede concentration. Killed creatures end the connection without a save;
 * PCs at zero vitality still check after their dying transition and damage reactions. */
export function finishDamageConcentration(ctx: JanusCtx, input: JanusInput, target: Row, status: Row, damage: number, killed: boolean,
  maimed?: { deferredMaimed: number } | { maimedBefore: number }): void {
  if (killed || int(ecLean(target, 'encounter_creature_dead')) === 1) {
    resolveConnectionCascade(ctx, status, ecId(target));
    insertRoll(ctx, { roll_type_1: 'concentration_broken', roll_item_id: CONCENTRATING, by: target });
    return;
  }
  let maimedDelta = 0;
  if (maimed && 'deferredMaimed' in maimed) {
    const stacks = damageMaimedStacks(ctx, target);
    maimedDelta = Math.max(0, Math.min(QUANTITY_FAMILIES[MAIMED_ID]!.max, stacks + maimed.deferredMaimed) - stacks);
  } else if (maimed) maimedDelta = damageMaimedStacks(ctx, target) - maimed.maimedBefore;
  concentrationCheck(ctx, input, target, status, damage, maimedDelta);
}
const FORTITUDE = '24C07918-39D5-4317-A958-BDF68B633A21';
const WEAPON_MELEE_ATTACK_ROLL = '2FE8DFB7-FE61-4862-AF71-3523262350A0';
const WEAPON_RANGED_ATTACK_ROLL = '3F85CA97-F9B2-A83A-FDE8-E523FD99346C';
/** apply_damage_to_target_inc.php's Maimed-on-crit block (lane A8). The save's roller is the TARGET (skill_roll_arr's caster_arr = $target_arr,
 *  roll_by the target, no roll_to, is_target, item_id_used_arr = the use's item); a failure applies Maimed with its own connection
 *  (status_connected_id ''), permanent, logged as status_effect_applied_to_target_after_contesting_roll_failed. A DRY RUN applies it whatever
 *  the face — the failure branch (a stack on a held Maimed, the 10th stack's overflow, the gain triggers) is then decided before any real die. */
function maimedOnCrit(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, weaponHit: boolean): void {
  if (!ecId(target)) handBack('apply_damage_to_target:maimed-target-not-a-member');
  const skill = selectSkillForContestingRoll(ctx, target, [FORTITUDE]);
  // the used item's roll_type (a natural weapon's is supplied by the attack phase, not its catalog definition)
  const usedRollType = upper(ctx.read.value(ability, 'roll_type')) || (weaponHit ? WEAPON_MELEE_ATTACK_ROLL : '');
  const result = skillRollArr(ctx, input, target, skill, null, 15,
    { rollType: DC_ROLL, asTarget: true, isTarget: true, usedRollType, houseRule: 'maimed_on_crit_fortitude_save' });
  if (result < 15 || ctx.dryRun) applyDamageMaimed(ctx, input, ability, caster, target);
}
/** The weapon/save lane and ability damage share Maimed's actual overflow consequence. */
export function applyDamageMaimed(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row): void {
  if (damageMaimedStacks(ctx, target) >= QUANTITY_FAMILIES[MAIMED_ID]!.max) {
    applyStatusEffect(ctx, input, ability, DEAD_STATUS_ID, 'status_effect_applied', caster, target,
      { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true);
    return;
  }
  applyStatusEffect(ctx, input, ability, MAIMED_ID, 'status_effect_applied_to_target_after_contesting_roll_failed', caster, target,
    { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true);
}
// StatusEffect ids (StatusEffect.php) — apply_player_dying.php's cascade.
const PRONE_ID = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';
const DYING_ID = '8AE129AF-52D8-CCE8-706A-85767DB52740';
const CRIPPLED_ID = '99C862B9-D6AE-4E18-7803-A6F9B42A88E1';
const MAIMED_ID = 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D';
// SpeedType::getId('speed_walking') (SpeedType.php).
/** VitalityStatus::getAll() ids — a required/forbidden status that is a vitality tier is read from computed.vitality.v_status
 * (mirrors ability-preconditions.ts's VITALITY_STATUS_IDS; check_post_damage_type_roll_conditions.php's ft_target_has_status). */
const HOSTED_EXTRA_VITALITY_STATUS_IDS = new Set(['82788F70-EBEF-44C4-80E7-6622AD20DD60', 'FF30B76E-785D-2CAF-FC66-C131D0548808', '658483AA-34DE-74E5-C974-E3E202C31CC7',
  'C97076EF-FED9-19B4-D109-C22F9BC740E8', '44B8792E-632E-2651-C666-67DFD181E92A', '298BE1DD-2917-476E-D79C-92CF8C598926',
  'D5DE14C4-3194-87BC-D767-6D45BDD90552', '8AE129AF-52D8-CCE8-706A-85767DB52740', '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA']);
/** v_status_id_of(): a bare UUID or a {items:{item_id}} shell. A vitality cluster without a readable tier is unknown, not empty. */
function vStatusIdOfTarget(target: Row): string | null {
  const vitality = record(record(target.computed)?.vitality);
  if (!vitality || !('v_status' in vitality)) return null;
  const raw = (vitality as Row).v_status;
  if (typeof raw === 'string') return upper(raw);
  const items = record(record(raw)?.items);
  const shellId = items ? scalar(items.item_id) : null;
  return typeof shellId === 'string' ? upper(shellId) : null;
}
/** check_post_damage_type_roll_conditions.php's ft_target_has_status, scoped to a hosted-extra fragment's own target gate.
 * null = undecidable (an unresolvable vitality tier) — the caller hands back rather than guess. */
function hostedExtraTargetHasStatus(ctx: JanusCtx, target: Row, id: string): boolean | null {
  if (!id) return false;
  if (HOSTED_EXTRA_VITALITY_STATUS_IDS.has(id)) {
    const vStatus = vStatusIdOfTarget(target);
    return vStatus === null ? null : vStatus === id;
  }
  // the rows held at PHP's draw (ctx.fragmentGateStatuses — before this use's own effects), else the live rows
  return (ctx.fragmentGateStatuses?.get(ecId(target)) ?? ctx.read.statuses(target)).some((status) => heldStatusItemId(status as Row) === id);
}
/** A damage path that runs post_effect_conditions BEFORE damageTypeRoll (PHP drew — and gated — the fragment first): call it before the
 *  effects to capture the target's rows; the returned runner holds them as ctx.fragmentGateStatuses around the damage call only. */
export function withFragmentGateStatuses(ctx: JanusCtx, target: Row): (damage: () => void) => void {
  const rows = [...ctx.read.statuses(target)];
  return (damage) => {
    const prior = ctx.fragmentGateStatuses;
    ctx.fragmentGateStatuses = new Map([...(prior ?? []), [ecId(target), rows]]);
    try { damage(); } finally { ctx.fragmentGateStatuses = prior ?? null; }
  };
}
function hostedExtraGateIds(ctx: JanusCtx, extra: Row, key: string): string[] {
  return ctx.read.entries(extra, key).map((entry) =>
    upper(typeof entry === 'string' ? entry : record(record(entry)?.items)?.item_id ?? record(entry)?.item_id));
}

/** Kill carriers target the fresh corpse; Self-range carriers and no-AoE passives target their owner. */
const ON_KILL = 'triggers_on_kill';
/** A due unsupported kill effect is skipped independently. The extreme previews still discover optional consent before dice. */
export function onKillGate(ctx: JanusCtx, caster: Row, target: Row, killDeathTail = false): void {
  zeroVitalityWitnessGate(ctx, target);
  if (!ctx.dryRun && consentPending(ctx.read)) handBack(`outcome:${TRIGGER_CONSENT_REASON}`);
  const killer = ecId(caster) ? ctx.members.get(ecId(caster)) : undefined;
  if (killer && ecId(killer) !== ecId(target)) { triggerFamilyPreDraw(ctx, killer, { trigger: ON_KILL, killDeathTail }, onKillGap, target); return; }
  if (rowOrPassivesHasAny(ctx, caster, [ON_KILL], 'calc_total_damage_and_apply_to_target:caster-passive-definition-not-held'))
    handBack('calc_total_damage_and_apply_to_target:unmodelled:triggers-on-kill');
  for (const status of ctx.read.statuses(caster)) {
    const definition = ctx.read.definition(String((status as Row).status_item_id ?? '')) ?? record((status as Row).status_arr);
    if (!definition) definitionNotHeld('calc_total_damage_and_apply_to_target:caster-status-definition-not-held', status.status_item_id);
    if (ctx.read.has(definition, ON_KILL)) handBack('calc_total_damage_and_apply_to_target:unmodelled:triggers-on-kill');
  }
}
/** apply_damage_to_target_inc.php ~L626: janus_trigger('triggers_on_kill', caster: the killer, targets: [victim], inciting: victim) —
 * after the death / dying decision, before the damage fan-out. */
export function onKillTriggers(ctx: JanusCtx, caster: Row, target: Row): void {
  const killer = ecId(caster) ? ctx.members.get(ecId(caster)) : undefined;
  // onKillGate already proved no carrier for a killer outside the member map / a self-kill
  if (killer && ecId(killer) !== ecId(target)) runTriggerFamily(ctx, { trigger: ON_KILL }, killer, target, [target], false, onKillGap);
  if (Number(ecLean(ctx.originals.get(ecId(target)) ?? target, 'v_current') ?? 0) > 0) zeroVitalityWitnessTriggers(ctx, target);
  actualDeathTriggers(ctx, target, caster);
  if (isAnimatedSummon(target)) triggerSummonDeath(ctx, target);
}
/** triggers_when_enemy_within_ft_reduced_to_zero_vitality (Warlock v13 Claim the Departing, 2026-10-07) — ft_dispatch_zero_vitality_witness_triggers()
 * (apply_damage_to_target_inc.php, right after triggers_on_kill in the same v_current <= 0 block). ft_zero_vitality_witness_ids(): every
 * roster member other than the victim, not dead, above 0 Vitality, on the victim's OPPOSING team, in roster order. Each gets one janus_trigger
 * frame with ITSELF as inciting + frame target (a Self-range reaction then lands on its holder); the victim rides the event for the
 * per-carrier radius gate (zeroVitalityWitnessAllows). */
/** ft_zero_vitality_witness_eligible(): not the victim, not dead, above 0 Vitality, on the victim's OPPOSING team — re-judged at dispatch. */
function zeroVitalityWitnessEligible(ctx: JanusCtx, row: Row, victim: Row): boolean {
  const id = ecId(row);
  if (!id || id === ecId(victim)) return false;
  const dead = ecLean(row, 'encounter_creature_dead');
  if (dead != null && dead !== 0 && dead !== '0' && dead !== false && dead !== '') return false;
  // the EC column is this use's authority (damage writes targetEc.v_current); the compiled bucket only for a shell row
  const current = ecLean(row, 'v_current') ?? record(record(row.computed)?.vitality)?.v_current;
  if (current == null) handBack('zero_vitality_witness:vitality-not-held');
  if (requiredInt(current, 'zero_vitality_witness:invalid-vitality') <= 0) return false;
  return janusCtxTriggerReaders(ctx).opposing(row, victim);
}
function zeroVitalityWitnesses(ctx: JanusCtx, victim: Row): Row[] {
  return [...ctx.members.values()].filter(row => zeroVitalityWitnessEligible(ctx, row, victim));
}
/** A witness this use did not certify, whose row proves no carrier can fire (zeroVitalityWitnessFact — the server's reach_watch fact plus
 *  the row's own statuses): janus_trigger collects nothing for it, so it is skipped instead of handing the use back (2026-10-08 — every
 *  lethal hit used to need each living enemy's full compile). */
function zeroVitalityWitnessSilent(ctx: JanusCtx, witness: Row): boolean {
  return !ctx.read.reactionInventoryComplete(witness) && zeroVitalityWitnessFact(janusCtxTriggerReaders(ctx), witness) === 'none';
}
/** PRE-DRAW: every carrier a witness could answer with must be one the executor runs (the victim is still alive here — a superset). */
function zeroVitalityWitnessGate(ctx: JanusCtx, victim: Row): void {
  for (const witness of zeroVitalityWitnesses(ctx, victim)) {
    if (zeroVitalityWitnessSilent(ctx, witness)) continue;
    triggerFamilyPreDraw(ctx, witness, { trigger: ZERO_VITALITY_WITNESS, zeroVitalityVictim: victim }, triggerExecutorGap, witness);
  }
}
function zeroVitalityWitnessTriggers(ctx: JanusCtx, victim: Row): void {
  for (const id of zeroVitalityWitnesses(ctx, victim).map(ecId)) {
    // Re-judged at dispatch (Codex review 2026-10-07): an earlier witness's reaction may have dropped or killed this one.
    const witness = ctx.members.get(id);
    if (!witness || !zeroVitalityWitnessEligible(ctx, witness, victim) || zeroVitalityWitnessSilent(ctx, witness)) continue;
    runTriggerFamily(ctx, { trigger: ZERO_VITALITY_WITNESS, zeroVitalityVictim: victim }, witness, witness, [witness], false);
  }
}
const BLUDGEONING_TYPE = '0C528CF8-1B5A-453C-9BDE-73CBBC239A2A';
/** damage_type_roll.php ~L476–500 for an extra fragment: its own (first) damage_type, else the caster's main_weapon_damage_type, else
 *  Bludgeoning (DamageType 'bludgeoning'). `raw` keeps the stored spelling for defendFragment; `owned` = the extra authors its type. */
export function extraFragmentType(ctx: JanusCtx, extra: Row, caster: Row): { id: string; raw: string; owned: boolean } {
  const first = ctx.read.entries(extra, 'damage_type')[0];
  const own = String(scalar(typeof first === 'string' ? first : record(record(first)?.items)?.item_id ?? record(first)?.item_id) ?? '').trim();
  if (own) return { id: upper(own), raw: own, owned: true };
  const main = String(scalar(ctx.read.value(caster, 'main_weapon_damage_type')) ?? '').trim();
  if (main) return { id: upper(main), raw: main, owned: false };
  return { id: BLUDGEONING_TYPE, raw: BLUDGEONING_TYPE, owned: false };
}
/** damage_type_roll.php ~L84–96: an extra fragment's `mod` through discern_variable_tags — a literal integer, or an essence tag
 *  ([body] / [mind] / [soul] / [spirit] / [fortune] — the caster's final essence via layer_value's bridge). Anything else → named. */
function fragmentMod(ctx: JanusCtx, raw: unknown, caster: Row): number {
  if (raw == null || raw === '') return 0;
  const text = String(scalar(raw) ?? '').trim();
  if (/^[+-]?\d+$/.test(text)) return parseInt(text, 10);
  const tag = /^\[(body|mind|soul|spirit|fortune)\]$/.exec(text)?.[1];
  if (!tag) handBack('damage_type_roll:hosted-extra-variable-dice-or-mod');
  const value = ctx.read.value(caster, tag!);
  if (value == null || value === '') handBack(`damage_type_roll:hosted-extra-essence-not-held:${tag}`);
  return requiredInt(value, 'damage_type_roll:hosted-extra-variable-dice-or-mod');
}
/** A hosted passive the page has not cached: named WITH its id (`…definition-not-held:<id>`), so the lane fetches that definition and resolves
 *  again (ability-truth.ts's pre-draw retry); an unreadable id keeps the old bare name. (2026-10-13 — Storm Currents' hosted rider.) */
/** A caster / target passive the page has not cached — the same id-carrying shape as hostedPassiveNotHeld. */
function passiveNotHeld(id: string): never {
  return /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id) ? handBack(`damage_type_roll:passive-definition-not-held:${id}`)
    : handBack('damage_type_roll:passive-definition-not-held');
}
function hostedPassiveNotHeld(id: string): never {
  return /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id) ? handBack(`damage_type_roll:hosted-passive-definition-not-held:${id}`)
    : handBack('damage_type_roll:unmodelled:hosted-passive-definition');
}
/** Every damage type a fragment of this use can roll (ft_damage_types_in_roll's superset): the primary's type, then each hosted
 * extra_damage_roll passive's (a typeless one: its fallback type — extraFragmentType). The pre-draw preview of the damage-TYPE triggers. */
function possibleFragmentTypes(ctx: JanusCtx, ability: Row, primaryType: string, caster?: Row): string[] {
  const out = new Set<string>(primaryType ? [upper(primaryType)] : []);
  for (const hosted of ctx.read.entries(ability, 'passive_ability')) {
    const hostedRow = record(hosted);
    const hostedId = upper(typeof hosted === 'string' ? hosted : hostedRow?.item_id ?? record(hostedRow?.items)?.item_id);
    const extra = record(hostedRow?.item_arr) ?? ctx.read.definition(hostedId);
    if (!extra) hostedPassiveNotHeld(hostedId);
    if (upper(ctx.read.value(extra, 'roll_type')) !== '9878714A-27C3-D737-88B3-E8C1EE63A20F') continue;
    for (const entry of ctx.read.entries(extra, 'damage_type')) {
      const id = upper(typeof entry === 'string' ? entry : record(record(entry)?.items)?.item_id ?? record(entry)?.item_id);
      if (id) out.add(id);
    }
    if (!ctx.read.entries(extra, 'damage_type').length) {
      if (!caster) handBack('damage_type_roll:typeless-hosted-extra-preview-caster');
      out.add(extraFragmentType(ctx, extra, caster!).id);
    }
  }
  return [...out];
}
/** POST-DAMAGE (apply_damage_to_target_inc.php ~L799–876, when damage landed): the damaged row's triggers_when_caster_takes_any_damage,
 * then its _damage_type, then — a creature attacker only — the attacker's triggers_when_target_takes_any_damage and _damage_type
 * (perspective inverted: that row owns the carriers, the damaged row incites). A PC dying still reacts (Fleshdobbin Shriek capture). */
/** An OBJECT attacker (a fired trap's host inventory row — janus-engine/traps/fire.ts, TRAPS 2026-09-28): PHP incites the victim's
 * reactions with the object row, and a Self-range reaction lands on the reactor. The executor dispatches with no inciting row, so a
 * carrier that targets the object or gates on its team is not modelled. Passives always target their holder. */
const objectAttackerGap = (ctx: JanusCtx, candidate: TriggerCandidate): string | null => triggerExecutorGap(ctx, candidate)
  ?? (upper(scalar(ctx.read.value(candidate.ability, 'range'))) !== RANGE_SELF
    && itemField(candidate.ability, 'item_type') !== 'passive_ability' ? 'object-attacker:target-not-self' : null)
  ?? (ctx.read.has(candidate.ability, 'effect_only_affects_opposing_team_type') || ctx.read.has(candidate.ability, 'effect_only_affects_same_team_type')
    ? 'object-attacker:team-gate' : null);
export function damageTakenTriggers(ctx: JanusCtx, input: JanusInput, caster: Row, target: Row, damageTypes: string[], targetJustKilled: boolean, critical = false): void {
  // The source package does not expose the persisted fixed-basis swarm ledger yet. A real surviving
  // damage application with a held swarm state/definition owes this duty; misses and replay saves do not.
  if (!targetJustKilled && !ctx.composedDeaths?.includes(ecId(target)) && Number(ecLean(target, 'v_current') ?? 0) > 0
      && (Number(ecLean(target, 'swarm_members') ?? 0) > 1
        || ['swarm_width_lanes', 'swarm', 'swarm_size'].some(key => ctx.read.has(target, key))))
    skipDeathDuty(ctx, target, 'Swarm damage body shedding is not yet supported: saved swarm details are not held.');
  // PHP re-seeds the persisted damage baseline before dispatch. Nested heals read this projection, then update it sequentially.
  patchVitalityCluster(target, Number(ecLean(target, 'v_current') ?? 0));
  const attackerId = upper(input.casterId);
  if (isInventoryRow(caster) && !ctx.members.has(attackerId)) {
    runTriggerFamily(ctx, { trigger: TAKES_ANY_DAMAGE, damageTypes }, target, null, [target], targetJustKilled, objectAttackerGap, true);
    runTriggerFamily(ctx, { trigger: TAKES_DAMAGE_TYPE, damageTypes, criticalHit: critical }, target, null, [target], targetJustKilled, objectAttackerGap, true);
    return;
  }
  const attacker = attackerId === ecId(target) ? target : ctx.members.get(attackerId) ?? null;
  runTriggerFamily(ctx, { trigger: TAKES_ANY_DAMAGE, damageTypes }, target, attacker, [target], targetJustKilled);
  runTriggerFamily(ctx, { trigger: TAKES_DAMAGE_TYPE, damageTypes, criticalHit: critical }, target, attacker, [target], targetJustKilled);
  const attackerMember = ecId(caster) ? ctx.members.get(ecId(caster)) : undefined;
  if (!attackerMember) return;
  runTriggerFamily(ctx, { trigger: TARGET_TAKES_ANY_DAMAGE, damageTypes }, attackerMember, target, [attackerMember], false);
  runTriggerFamily(ctx, { trigger: TARGET_TAKES_DAMAGE_TYPE, damageTypes, criticalHit: critical }, attackerMember, target, [attackerMember], false);
}

/**
 * apply_damage_to_target() with a PRECOMPUTED total and no damage roll — the push collision's half damage on the creature that was
 * struck (resolve_push_collision_with_inventory.php apply_half_collision_damage_to_struck, Session A2 R-108 bucket 2): applied RAW (no
 * resistance, no damage row, suppress_shield_shattering_blow), temporary vitality first; then — as apply_damage_to_target does after the
 * vitality write — the sleeper wakes, the damage-taken fan-out runs (no damage types: the precomputed total carries none) and a
 * Concentrating target saves. A creature's lethal hit uses the raw death tail below. A lethal PC, a by-id damage branch status,
 * or an object stays outside this raw lane. Every hand-back here is decided on state
 * or reached by the extreme-dice dry runs (the struck die is drawn like any other).
 */
/** The status portion of a damage application. Weapon attacks reuse this plan on their isolated consequence frame. */
export function planDamageStatusEffects(ctx: JanusCtx, caster: Row, target: Row) {
  const statuses = activeStatuses(ctx, target);
  let concentrating: Row | null = null;
  for (const status of statuses) {
    const definition = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
    if (!definition) definitionNotHeld('apply_damage_to_target:raw:status-definition-not-held', statusItemId(status));
    if (damageStatusScope(status, definition, ctx.read) === 'inert') continue;
    if (statusItemId(status) === CONCENTRATING) {
      if (ctx.concentrationBreaks === undefined) handBack('calc_total_damage_and_apply_to_target:unmodelled:concentration-check');
      if (!/^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(String(statusScalar(status, 'status_id') ?? ''))) handBack('check_concentration:status-id-not-held');
      concentrating ??= status;
    }
  }
  return { wake: planWake(ctx, target), charm: planCharmBreak(ctx, target), retry: planContestRetry(ctx, caster, target), concentrating,
    reveal: statuses.some(status => statusItemId(status) === HIDDEN) ? [HIDDEN, SNEAKING].flatMap(id => statuses.filter(status => statusItemId(status) === id)) : [],
    contingencies: statuses.filter(status => CONTINGENCY_ARMING_IDS.includes(statusItemId(status))) };
}

/** Damage contingencies, then native reveal and charm removal. */
export function performDamageStatusEffects(ctx: JanusCtx, input: JanusInput, caster: Row, target: Row, plan: ReturnType<typeof planDamageStatusEffects>,
  before: number): void {
  fireDamageContingencies(ctx, target, before);
  for (const status of plan.reveal) {
    if (!ctx.statusesRemoved.some(row => upper(row.status_id) === upper(statusScalar(status, 'status_id'))))
      removeStoredStatus(ctx, target, status, 'single');
  }
  if (plan.reveal.length) insertRoll(ctx, { roll_type_1: 'target_creature_revealed', by: caster, to: target });
  if (plan.charm) performCharmBreak(ctx, input, plan.charm, target);
}

export function applyRawDamageToCreature(ctx: JanusCtx, input: JanusInput, caster: Row, target: Row, total: number): void {
  if (!ecId(target) || isInventoryRow(target)) handBack('apply_damage_to_target:raw:object-target');
  if (!Number.isSafeInteger(total) || total < 0) handBack('apply_damage_to_target:raw:invalid-total');
  // lane A12 (coordinator Round 24 P2): a SECOND raw hit on a creature this use already raw-struck hands back on the state alone, before any
  // face decides whether it lands — the zero-half return below used to run first, so the all-minimum dry pass never met the
  // after-earlier-change compare and two 1-face collisions drew real dice before handing back (tape parity)
  const rawStruck = (ctx.rawStruck ??= new Set<string>());
  // OWNER RULING 2026-10-03 (lethal push collisions — lane A11): a creature this use already killed takes no further damage — decided before
  // any of the application's own preflights (A11 Codex review Q3) AND before the second-raw-hit hand-back (coordinator Round 30 P2 #1, lane
  // A13: a second collision on a creature the first collision KILLED handed the cast back, and legacy then logged a second kill)
  if (ctx.composedDeaths?.includes(ecId(target))) { rawStruck.add(ecId(target)); return; }
  if (rawStruck.has(ecId(target))) handBack('apply_damage_to_target:raw:second-raw-hit');
  rawStruck.add(ecId(target));
  if (total <= 0) return;
  const statusPlan = planDamageStatusEffects(ctx, caster, target);
  const { wake, retry, concentrating } = statusPlan;
  const inciting = ecId(caster) ? ctx.members.get(ecId(caster)) ?? null : null;
  triggerFamilyPreDraw(ctx, target, { trigger: TAKES_ANY_DAMAGE, damageTypes: [] }, undefined, inciting);
  triggerFamilyPreDraw(ctx, target, { trigger: TAKES_DAMAGE_TYPE, damageTypes: [], criticalHit: false }, undefined, inciting);
  const attackerMember = ecId(caster) ? ctx.members.get(ecId(caster)) : undefined;
  if (ecId(caster) && !attackerMember) handBack('apply_damage_to_target:raw:attacker-not-member');
  if (attackerMember) {
    triggerFamilyPreDraw(ctx, attackerMember, { trigger: TARGET_TAKES_ANY_DAMAGE, damageTypes: [] }, undefined, target);
    triggerFamilyPreDraw(ctx, attackerMember, { trigger: TARGET_TAKES_DAMAGE_TYPE, damageTypes: [], criticalHit: false }, undefined, target);
  }
  const vitality = record(record(target.computed)?.vitality);
  const tempBefore = requiredInt(vitality?.v_temp ?? ecLean(target, 'v_temp'), 'damage_type_roll:temp-vitality-not-held');
  requireTemporaryVitalityLane(ctx, target, tempBefore, total >= tempBefore);
  const before = requiredInt(ecLean(target, 'v_current'), 'damage_type_roll:vitality-not-held');
  const applied = Math.max(0, total - tempBefore);
  // lane A11 (A11 Codex review Q4 P2): a raw hit on a creature whose vitality THIS use already moved (an earlier raw half, the cast's own damage)
  // may be lethal only on an intermediate face the extreme dry runs never meet (the all-max run kills it earlier, the all-min run leaves it
  // standing) — hand back on the state, lethal or not, so the run that reaches it (the all-min one) decides it before any real die
  const originalRow = ctx.originals.get(ecId(target));
  // (A11 Codex review Q5 P2) temporary vitality too: an earlier hit wholly absorbed by v_temp leaves v_current unchanged
  if (!ctx.read.deathResources && (!originalRow || String(ecLean(originalRow, 'v_current')) !== String(before) || tempOf(originalRow) !== String(tempBefore)))
    handBack('apply_damage_to_target:raw:after-earlier-change');
  if (before <= applied) {
    rawLethal(ctx, input, caster, target, applied, tempBefore, total, wake);
    if (applied > 0) performDamageStatusEffects(ctx, input, caster, target, statusPlan, before);
    return;
  }
  const targetEc = ec(target);
  absorbTemporaryVitality(ctx, target, total, tempBefore);
  if (requiredInt(ecLean(target, 'v_current'), 'damage_type_roll:vitality-not-held') !== before)
    handBack('lost_shapeshift:lane-cannot-persist');
  targetEc.v_current = before - applied;
  patchVitalityCluster(target, targetEc.v_current as number);
  if (wake && applied > 0) performWake(ctx, wake, caster, target, false);
  if (applied > 0) performDamageStatusEffects(ctx, input, caster, target, statusPlan, before);
  if (retry && applied > 0) performContestRetry(ctx, input, retry, target);
  if (applied > 0) damageTakenTriggers(ctx, input, caster, target, [], false);
  if (concentrating) {
    if (ctx.statusesRemoved.some(change => change.target === ecId(target) && upper(change.status_item_id) === CONCENTRATING))
      handBack('check_concentration:concentration-removed-this-use');
    concentrationCheck(ctx, input, target, concentrating, total);
  }
}

/** Raw collision hits use the same actual-outcome death/Dying row composer. */
/** A row's temporary vitality as the raw path reads it (the compiled cluster first, then the column), as a comparable string. */
const tempOf = (row: Row): string => String(Number(record(record(row.computed)?.vitality)?.v_temp ?? ecLean(row, 'v_temp') ?? 0) || 0);
function rawLethal(ctx: JanusCtx, input: JanusInput, caster: Row, target: Row, applied: number, tempBefore: number, total: number,
  wake: ReturnType<typeof planWake>): void {
  const ability = ctx.read.definition(input.abilityId);
  if (!ability) handBack('apply_damage_to_target:raw:ability-not-held');
  const targetEc = ec(target);
  absorbTemporaryVitality(ctx, target, total, tempBefore);
  const original = ctx.originals.get(ecId(target));
  if (!original || String(ecLean(target, 'v_current')) !== String(ecLean(original, 'v_current')))
    handBack('lost_shapeshift:lane-cannot-persist');
  targetEc.v_current = 0;
  patchVitalityCluster(target, 0);
  fireDamageContingencies(ctx, target, Number(ecLean(original, 'v_current')));
  const lethal = Number(ecLean(target, 'v_current')) <= 0;
  if (lethal) resolvePlayerDying(ctx, input, ability, caster, target, applied);
  if (wake && applied > 0) performWake(ctx, wake, caster, target, lethal);
  if (applied > 0) damageTakenTriggers(ctx, input, caster, target, [],
    ctx.composedDeaths?.includes(ecId(target)) === true && String(itemField(target, 'item_type') ?? ecLean(target, 'item_type')) !== 'player_character');
}

/** includes/functions/janus/rolls/damage_type_roll.php:damage_type_roll (single ordinary fragment) */
const MAX_DAMAGE_VS_CT = 'maximum_damage_against_creature_type';
function maxDamageProviderEntryId(entry: unknown): string {
  if (typeof entry === 'string') return upper(entry);
  const e = record(entry);
  return upper(e?.item_id ?? itemField(record(e?.items), 'item_id') ?? itemField(record(e?.item_arr), 'item_id'));
}
/**
 * check_damage_resistance_and_immunity.php for ONE fragment's damage type: resistance → immunity → vulnerability, first
 * matching entry per layer (the compiled defence store; the weapon lane's matchDefenseEntry / defenseMeta — S6, reviewed),
 * each applied to the fragment result and logged as its tuple on the fragment. Spell flags follow this step.
 * PHP compares the RAW stored id; a non-canonical id hands back.
 */
export function defendFragment(ctx: Pick<JanusCtx, 'read'>, target: Row, damageTypeId: string, damageTypeName: string, value: number, faces: unknown[],
  zero?: { reason: string | null },
  /** The caster's ignore_damage_type_resistance ids (upper-cased): a matched resistance of a listed type is logged ignored, not applied. */
  ignoreResistance?: ReadonlySet<string>, /** Only this fragment's own effect flag applies. */ fragment?: Row): number {
  let result = value;
  // The six first-wins passes in PHP's order: the typed family, then the OBJECT family — composition_rollup_layer(): the row's own
  // entries, then each item_composition's (R2 ask 1; PHP runs it for every target, a creature with no such layers is a no-op).
  const passes: Array<{ layer: string; kind: 'resistance' | 'immunity' | 'vulnerability'; entries: unknown[] }> = [
    ...MITIGATION_LAYERS.map(({ layer, kind }) => ({ layer, kind, entries: ctx.read.entries(target, layer) })),
    ...OBJECT_DEFENSE_LAYERS.map(({ layer, kind }) => ({ layer, kind, entries: compositionRollupEntries(ctx, target, layer) })),
  ];
  for (const { layer, kind, entries } of passes) {
    if (!entries.length) continue;
    if (damageTypeId !== upper(damageTypeId)) handBack('check_damage_resistance_and_immunity:damage-type-id-case');
    const entry = matchDefenseEntry(entries, damageTypeId);
    if (!entry) continue;
    const meta = defenseMeta(kind, layer, damageTypeId, entry);
    if (kind === 'resistance' && ignoresDamageTypeResistance(damageTypeId, ignoreResistance, fragment, ctx.read)) {
      // check_damage_resistance_and_immunity.php's ignore branch: the ρ tuple, meta kind 'resistance_ignored' (source kept), no halving
      faces.push(['ρ', `${damageTypeName} Resistance Ignored`, { ...meta, kind: 'resistance_ignored' }]);
    } else if (kind === 'resistance') {
      result = Math.floor(result / 2);
      if (zero && result === 0 && !zero.reason) zero.reason = 'fully resisted';
      faces.push(['0.5 Ω', `${damageTypeName} Resisted`, meta]);
    } else if (kind === 'immunity') {
      result = 0;
      if (zero) zero.reason = 'immune';
      faces.push(['θ', `${damageTypeName} Negated`, meta]);
    } else { result *= 2; faces.push(['2 λ', `${damageTypeName} Vulnerability`, meta]); }
  }
  return result;
}
type SpellDamageFlag = 'spell_damage_resistance' | 'spell_damage_vulnerability';
const SPELL_DAMAGE_VULNERABILITY_PASSIVE = '36EBBFC0-12FB-B13C-50E7-25A6AF89BAA3';
/** The target's native root/compiled passive buckets, then grants on held statuses (the existing damage gate's walk). */
function damageTargetPassiveEntries(ctx: JanusCtx, target: Row): unknown[] {
  const compiled = record(record(record(target.computed)?.creature)?.abilities)?.passive_ability;
  if (compiled != null && !Array.isArray(compiled)) handBack('damage_type_roll:passive-carriers-not-held');
  return [...ctx.read.entries(target, 'passive_ability'), ...(Array.isArray(compiled) ? compiled : []),
    ...activeStatuses(ctx, target).flatMap(status => {
      const def = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
      return def ? ctx.read.entries(def, 'passive_ability') : [];
    })];
}
/** An unstamped passive evaluates its own conditions; only unreadable conditions retain the old passive refusal. */
function spellPassiveConditionsMet(ctx: JanusCtx, target: Row, def: Row, flag: SpellDamageFlag): boolean {
  const gap = (): never => handBack(`calc_total_damage_and_apply_to_target:unmodelled:passive.${flag}`);
  for (const key of [...Object.keys(def), ...Object.keys(record(def.item_layer_types) ?? {})])
    if (key.startsWith('if_') && !isAbilityConditionLayer(key) && ctx.read.has(def, key)) gap();
  const host = { ...target, statuses: { ...record(target.statuses), target: activeStatuses(ctx, target) } };
  const conditions = createAbilityConditionEvaluator({ catalogRows: C1_CONTRACT.catalogRows, abilityTypes: C1_CONTRACT.abilityTypes,
    layerReaders: { layerValue: (row, key) => row ? ctx.read.value(row as Row, key) ?? null : null,
      layerEntries: (row, key) => row ? ctx.read.entries(row as Row, key) : [] },
    aggregationStrategy: name => C1_CONTRACT.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
    resolveCarrierRef: id => ctx.read.definition(upper(id)) ?? gap(), queryTemplateDefinition: ctx.read.definition,
    resolveInventoryInstance: id => findInventoryRow(host, id) ?? gap(), equipmentSlots: C1_CONTRACT.equipmentSlots,
    vitalityStatusIds: C1_CONTRACT.vitalityStatusIds, skillEntries: C1_CONTRACT.skillEntries,
    conditionSource: (kind, args) => {
      const members = [...ctx.members.values()].map(row => row === target ? host : row);
      const held = ctx.read.possessorScene?.(host, members)?.conditionScene?.();
      if (kind === 'present-light' && ctx.read.lightTypesPresent) return ctx.read.lightTypesPresent(host, members);
      return answerConditionContext(kind, args, { encounter: held?.encounter ?? { ...ctx.encounter, encounter_creatures_arr: members },
        campaign: held?.campaign, value: ctx.read.value, entries: ctx.read.entries, definition: ctx.read.definition,
        abilityTypes: C1_CONTRACT.abilityTypes, equipmentSlots: C1_CONTRACT.equipmentSlots });
    } });
  try { return conditions.check(def, host); }
  catch (error) { if (error instanceof ConditionContextUnknown) gap(); throw error; }
}
/** Direct sources win; a scalar compiled flag takes provenance from a held status before falling back to anonymous. */
export function spellDamageFlagSource(ctx: JanusCtx, target: Row, flag: SpellDamageFlag): Row | null {
  const enabled = (entry: unknown): boolean => {
    const row = record(entry);
    return phpJsonTruthy(row ? row.item_layer_effect ?? row[0] ?? null : Array.isArray(entry) ? entry[0] : entry);
  };
  const source = (row: Row): Row => Object.fromEntries(['source_item_id', 'source_name', 'source_kind']
    .map(key => [key, row[key]]).filter(([, value]) => value != null && value !== ''));
  const definitionSource = (def: Row): Row => source({ source_item_id: itemField(def, 'item_id'), source_name: itemField(def, 'item_name'),
    source_kind: itemField(def, 'item_type') ?? 'passive_ability' });
  const direct = ctx.read.entries(target, flag).find(enabled);
  const directSource = direct === undefined ? null : source(record(direct) ?? {});
  if (directSource && Object.keys(directSource).length) return directSource;
  const statuses = activeStatuses(ctx, target), held = new Set(statuses.map(statusItemId));
  for (const status of statuses) {
    const def = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
    if (def && ctx.read.entries(def, flag).some(enabled)) return definitionSource(def);
  }
  if (directSource) return directSource;
  for (const entry of damageTargetPassiveEntries(ctx, target)) {
    if (!passiveEntryActive(entry)) continue;
    const row = record(entry), id = abilityEntryId(entry);
    if (id === SPELL_DAMAGE_VULNERABILITY_PASSIVE) {
      if (flag === 'spell_damage_vulnerability') return { source_item_id: id, source_name: 'Spell Damage Vulnerability', source_kind: 'passive_ability' };
      continue;
    }
    const inline = record(row?.item_arr) ?? row;
    const def = inline && record(inline.item_layer_types) ? inline : ctx.read.definition(id);
    if (!def) passiveNotHeld(id);
    if (!ctx.read.entries(def, flag).some(enabled)) continue;
    if ((!row || scalar(ctx.read.value(row, 'built_from_item_type')) !== 'status_effect')
        && (isAuraPassive(def, (node, key) => node ? ctx.read.value(node as Row, key) ?? null : null) || hasTriggerKey(def))
        && !held.has(upper(itemField(def, 'item_id') ?? id))) continue;
    if (!(row && 'passive_conditions_active' in row) && !spellPassiveConditionsMet(ctx, target, def, flag)) continue;
    return definitionSource(def);
  }
  return null;
}
/** After typed defences, only spell/curse provenance applies: floor-half, then double, retaining the first zero reason. */
export function defendSpellFragment(ctx: JanusCtx, target: Row, outer: Row, fragment: Row, value: number, faces: unknown[],
  zero?: { reason: string | null }): number {
  if (![itemField(outer, 'item_type'), fragment.source_item_type, fragment.source_kind].some(type => type === 'spell' || type === 'curse')) return value;
  let result = value;
  for (const flag of ['spell_damage_resistance', 'spell_damage_vulnerability'] as const) {
    const source = spellDamageFlagSource(ctx, target, flag);
    if (source === null) continue;
    const resistance = flag === 'spell_damage_resistance';
    result = resistance ? Math.floor(result / 2) : result * 2;
    if (resistance && zero && result === 0 && !zero.reason) zero.reason = 'fully resisted';
    faces.push([resistance ? '0.5 Ω' : '2 λ', resistance ? 'Spell Resisted' : 'Spell Vulnerability',
      { kind: resistance ? 'spell_resistance' : 'spell_vulnerability', defense_layer: flag, ...source }]);
  }
  return result;
}
const OBJECT_DEFENSE_LAYERS = [
  { layer: 'object_damage_type_resistance', kind: 'resistance' },
  { layer: 'object_damage_type_immunity', kind: 'immunity' },
  { layer: 'object_damage_type_vulnerability', kind: 'vulnerability' },
] as const;
/** layer_list_entry_uuid() (case kept — PHP dedupes on the raw string). */
function listEntryUuid(entry: unknown): string {
  const cut = (value: unknown): string => typeof value === 'string' && value !== '' ? value.split('|', 1)[0]! : '';
  const row = record(entry);
  if (row) return cut(row.item_layer_effect) || cut(record(row.items)?.item_id) || cut(row.item_id);
  return cut(entry);
}
/** composition_rollup_layer(): own entries, then each item_composition's (a nested definition with layers, else the catalog one by
 * id — the page's rows are lean), deduped by layer_list_entry_uuid ('' never dedupes). An unresolvable composition hands back. */
export function compositionRollupEntries(ctx: Pick<JanusCtx, 'read'>, target: Row, layer: string): unknown[] {
  const lists: unknown[][] = [ctx.read.entries(target, layer)];
  for (const entry of ctx.read.entries(target, 'item_composition')) {
    const nested = record(entry);
    const definition = nested && record(nested.item_layer_types) ? nested : ctx.read.definition(upper(listEntryUuid(entry)));
    // named with its id so the lane fetches the composition and resolves again (a lean target row carries the id only)
    if (!definition) definitionNotHeld('check_damage_resistance_and_immunity:composition-definition-not-held', listEntryUuid(entry));
    lists.push(ctx.read.entries(definition, layer));
  }
  const out: unknown[] = []; const seen = new Set<string>();
  for (const list of lists) for (const entry of list) {
    const id = listEntryUuid(entry);
    if (id !== '' && seen.has(id)) continue;
    if (id !== '') seen.add(id);
    out.push(entry);
  }
  return out;
}
/** ft_exploding_dice_threshold_for_link(). */
export function explodeThreshold(die: number, fortune: number, link: number): number {
  if (link <= 1) return die;
  return Math.max(2, die - Math.max(0, fortune - 2)) + (link - 2);
}
/** damage_type_roll.php's exploding-dice pass for ONE fragment: ft_exploding_dice_extra_rolls() per KEPT die, in tuple (rsorted) order;
 *  the minted dice append after the walk and add to the result only (the fragment's `dice` — crit bonus / upcast — is untouched). */
function explodeFragmentDice(ctx: JanusCtx, faces: readonly DieTuple[], die: number, fortune: number,
  meta: Record<string, unknown>): { tuples: unknown[]; total: number } {
  const tuples: unknown[] = [];
  let total = 0;
  if (die < 3) return { tuples, total };
  for (const [value, , faceMeta] of faces) {
    if (!faceMeta?.kept) continue;
    let seed = Math.max(1, Math.min(die, value));
    for (let link = 1, n = 0; n < 24; link++) {
      const threshold = explodeThreshold(die, fortune, link);
      if (threshold > die || seed < threshold) break;
      const next = ctx.roll.d(die); n++;
      tuples.push([next, `d${die} EXPLODE ${link + 1} (needed ${threshold}+)`,
        { ...meta, kind: 'explosion', explosion_link: link + 1, explosion_threshold: threshold, explosion_die: die }]);
      total += next; seed = next;
    }
  }
  return { tuples, total };
}
// StatusEffect ids the damage path acts on BY ID for any target (R2 ask 7): apply_damage_to_target wakes Sleeping (its connections
// and a log row) and reveals Hidden (a target_*_revealed row). Mirror Image is consumed by K4's on-hit tail;
// Shielded consumption is shared with the weapon lane.
const DAMAGE_BRANCH_TARGET_STATUS_IDS: ReadonlySet<string> = new Set([
  // sleeping (095BC6BC…): served since R-113 (Session A step 18) — statuses/wake-on-damage.ts
  // charmed / dominated: the damage break is planCharmBreak (statuses/retry-contest-on-damage.ts)
  '47667406-1B72-9D60-310B-D8CF6BFE397D', // hidden
]);
function maxDamageProviderGuard(ctx: JanusCtx, ability: Row, caster: Row): void {
  const type = String(itemField(ability, 'item_type') ?? '');
  if (type !== 'weapon' && type !== 'natural_weapon') return;
  const carries = (entry: unknown): void => {
    const id = maxDamageProviderEntryId(entry);
    const def = id ? ctx.read.definition(id) : null;
    if (!def) handBack('max_damage_vs_creature_type:provider-not-held');
    if (ctx.read.has(def, MAX_DAMAGE_VS_CT)) handBack('max_damage_vs_creature_type:weapon-provider');
  };
  for (const bucket of ['passive_ability', 'ability']) for (const entry of ctx.read.entries(ability, bucket)) carries(entry);
  for (const entry of ctx.read.entries(caster, 'passive_ability')) {
    const source = String(record(entry)?.source ?? '');
    if (/^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(source)) carries(entry);
  }
}

/** A held status is subtracted through the compiled total only when that total names it as a source.
 * An elided kind is proved by its mitigation category, independently of the held ability's catalog type. */
function compiledMitigationSource(target: Row, statusItemId: string): boolean {
  const sources = record(record(record(record(target.computed)?.creature)?.ac)?.mitigation)?.sources;
  return statusItemId !== '' && Array.isArray(sources)
    && sources.some((source) => upper(record(source)?.href_id) === statusItemId
      && (record(source)?.kind === 'status_effect'
        || record(source)?.kind == null && record(source)?.category === 'damage_mitigation'));
}
const DAMAGE_IGNORES_ALL_MITIGATION = 'damage_ignores_all_damage_mitigation';
/** All-damage ignore also reaches Object Damage Mitigation; EC-tier Damage Reduction still applies. */
export function damageIgnoresAllMitigation(ctx: JanusCtx, ability: Row, caster: Row): boolean {
  return damageProviderFlag(ctx.read, ctxDamageProviders(ctx, caster, ability, new Set([DAMAGE_IGNORES_ALL_MITIGATION])), DAMAGE_IGNORES_ALL_MITIGATION);
}

function damageFormulaDice(ctx: JanusCtx, input: JanusInput, caster: Row, raw: unknown): unknown {
  const value = variableDice(raw, input.useAbilityVariable1);
  return typeof value === 'string' && value.includes('[')
    ? discernVariableTags(ctx, value, caster, { x: input.useAbilityVariable1, reason: 'damage_type_roll:dice-formula', integer: true })
    : value;
}

export function damageTypeRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, half = false,
  weaponHit = false, ecDamageReduction = 0, critical = false,
  /** R-108 push collision (lane A4): PHP's damage_type_rolls PREPARES the fragment (draws its dice, its typed defences) BEFORE
   *  post_effect_conditions; calc_total applies it after. A caller passes the effects here: they run between preparation and application,
   *  and `false` skips the application (conditions unmet). */
  between?: () => boolean, attackAbility?: Row): void {
  if (deadDamageTargetSkips(ctx, ability, target)) return;
  // damage_roll.php fires the on-hit hooks before preparing/applying damage, including a killing blow.
  runK4OnHit(ctx, input, ability, caster, target, critical,
    upper(ctx.read.value(ability, 'roll_type')) || (weaponHit ? '2FE8DFB7-FE61-4862-AF71-3523262350A0' : ''),
    { validateRollHook: () => { rollTypeSourceStacks(ctx, ability, caster, target,
      upper(ctx.read.value(ability, 'roll_type')) === DOT_ROLL ? DOT_ROLL : DAMAGE_ROLL, upper(itemField(ability, 'item_id'))); } });
  // damage_type_roll.php: a fragment is a Damage-Over-Time tick when the item's own roll_type is damage_over_time_roll; every
  // other fragment is a Damage Roll, which the campaign's exploding-dice house rule can detonate (not ported — pre-draw).
  const damageOverTime = upper(ctx.read.value(ability, 'roll_type')) === DOT_ROLL;
  const houseRules = damageOverTime && !critical ? null : ctx.read.houseRules?.() ?? null;
  if ((!damageOverTime || critical) && !houseRules) handBack('damage_type_roll:house-rules-not-held');
  const types = ctx.read.entries(ability, 'damage_type').map((entry) =>
    upper(typeof entry === 'string' ? entry : record(record(entry)?.items)?.item_id ?? record(entry)?.item_id));
  // damage_type_roll.php ~L476: a damage-type PICK (use_ability_variable_1 naming a damage_type item — user_choice_1_damage_type) sets the
  // fragment's type. Janus admits an authored type; slot 2 can accompany an X amount and also retypes its extra fragments.
  const pick = damageTypePickOf(ctx, attackAbility ?? ability, input.useAbilityVariable1, input.useAbilityVariable2);
  const extraPick = nextSpellPlan(ability)?.damageType
    ?? (input.useAbilityVariable2 != null && isVariableAmount(ctx, attackAbility ?? ability, input.useAbilityVariable1) ? pick : null);
  if (pick) types[0] = pick;
  // PHP layer_value('damage_type') deliberately selects the first entry of a
  // multi-valued layer (build_item_arr.php); later entries are not fragments.
  // damage_type_roll.php: a natural weapon without an authored damage_type uses
  // the caster's compiled main weapon type. The attack phase supplies the melee
  // roll type on its runtime item_arr, although the catalog definition omits it.
  if (weaponHit && !types[0]) types[0] = upper(ctx.read.value(caster, 'main_weapon_damage_type'));
  if (!types[0]) handBack('damage_type_roll:missing-type');
  // Typed/object defences, spell flags and mitigation resolve below; these remaining target modifiers retain their gaps.
  for (const key of ['all_incoming_damage_changed_by_percentage', 'damage_reduction', 'damage_taken_pct_mod', 'reduce_damage_roll',
    'warding_bond', 'damage_redirect',
    'triggers_when_target_takes_damage'])
    if (ctx.read.has(target, key)) handBack(`damage_type_roll:target:${key}`);
  const CASTER_KEYS = ['damage_type_reroll_1s_and_2s', 'damage_type_roll_1_becomes_2',
    'ignore_damage_type_resistance', 'ignore_damage_type_immunity'];
  const outputPercentage = Number(scalar(ctx.read.value(caster, 'all_damage_output_changed_by_percentage')) ?? 0);
  if (!Number.isFinite(outputPercentage)) handBack('damage_type_roll:damage-percentage-unreadable');
  const objectTarget = isInventoryRow(target) || (!!ecId(target) && ecId(target) === inventoryId(target));
  const attackHit = weaponHit || SPELL_ATTACK_ROLL_TYPES.has(upper(ctx.read.value(attackAbility ?? ability, 'roll_type')));
  const hasMitigation = Number(scalar(ctx.read.value(target, 'damage_mitigation'))) > 0;
  const providerLayers = new Set<string>();
  if (hasMitigation || objectTarget && Number(scalar(ctx.read.value(target, 'object_damage_mitigation'))) > 0)
    providerLayers.add(DAMAGE_IGNORES_ALL_MITIGATION);
  if (hasMitigation) {
    providerLayers.add('ignore_damage_mitigation_damage_type');
    if (attackHit) { providerLayers.add('attacks_ignore_damage_mitigation'); providerLayers.add(ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION); }
    if (String(itemField(ability, 'item_type') ?? '') === 'spell') providerLayers.add('spells_ignore_damage_mitigation');
  }
  if (objectTarget) providerLayers.add('damage_to_objects_multiplier');
  const providers = ctxDamageProviders(ctx, caster, ability, providerLayers);
  const providerFlag = (key: string): boolean => damageProviderFlag(ctx.read, providers, key);
  const ignoresAllMitigation = providerFlag(DAMAGE_IGNORES_ALL_MITIGATION);
  const ignoresMitigation = ignoresAllMitigation || attackHit && providerFlag('attacks_ignore_damage_mitigation')
    || String(itemField(ability, 'item_type') ?? '') === 'spell' && providerFlag('spells_ignore_damage_mitigation');
  const exemptMitigationTypes = new Set(ignoredMitigationTypes(ctx.read, providers));
  // ignore_damage_type_resistance on the CASTER (2026-10-08 follow-up, Perfect Breach BAFC2775 — check_damage_resistance_and_immunity.php):
  // the row's own list (bucket-first layer read) UNION the root slot finalize_creature_arr folds a passive grant into, upper-cased; a matched
  // Damage Type Resistance (typed or object) of a listed type is logged "<type> Resistance Ignored" instead of halving. Immunity unaffected.
  // A status-carried list still hands back below (layer_on_status_effects' flatten is not modelled).
  const casterIgnoresResistance = new Set([...ctx.read.entries(caster, 'ignore_damage_type_resistance'),
    ...(Array.isArray(caster.ignore_damage_type_resistance) ? caster.ignore_damage_type_resistance as unknown[] : [])]
    .map((entry) => upper(listEntryUuid(entry))).filter(Boolean));
  for (const key of CASTER_KEYS) {
    if (key === 'ignore_damage_type_resistance') continue;
    if (ctx.read.has(caster, key)) handBack(`damage_type_roll:caster:${key}`);
  }
  // layer_on_status_effects() flattens a status's layers onto its holder in PHP; a page row may carry them only inside the status
  // definition (R2 oracle, 2026-09-28: a status-carried reroll / transfer / ADV answered un-applied). Judge the definitions too.
  const casterStatusRows = activeStatuses(ctx, caster);
  const casterStatusDefs = casterStatusRows.map((status) => {
    const def = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!def) definitionNotHeld('damage_type_roll:caster-status-definition-not-held', status.status_item_id);
    return def;
  });
  for (const def of casterStatusDefs) for (const key of CASTER_KEYS)
    if (ctx.read.has(def, key)) handBack(`damage_type_roll:caster-status:${key}`);
  const fragmentRollType = damageOverTime ? DOT_ROLL : DAMAGE_ROLL;
  // Legacy damage_type_roll.php:273-280: item + caster + target-against stacks, clamped to ±5 per fragment.
  const advDisStacks = rollTypeSourceStacks(ctx, ability, caster, target, fragmentRollType, upper(itemField(ability, 'item_id')));
  const resultModifiers = rollTypeFragmentModifiers(ctx, caster, target, fragmentRollType);
  // Wizard v9 damage_type_is_rolled_at_max (caster-spell-layers.ts): the damage types the CASTER (root / passive / feat / ACTIVE status —
  // Avatar of Evocation's combat-start self-status) rolls at max, settled pre-draw for the primary and every extra fragment. Max wins over a
  // roll-type min (rollTypeFragmentModifiers' own precedence). Not stamped into castForcedRollExtreme: a later tick re-reads the caster.
  const typesAtMax = ctxDamageTypesRolledAtMax(ctx, caster);
  const primaryTypeMax = typesAtMax.get(types[0]!) ?? null;
  const doubleSources = casterDoubleSource(ctx, caster, target, ability, fragmentRollType);
  // PHP keeps the first caster-side fragment verdict for a later ticking status stamp, even if an Empowered source
  // is consumed between the fragment and its application. The uncommitted nonzero stamp hands back at apply-status.
  const castExtreme = ctx.castForcedRollExtreme ??= {};
  if (!Object.prototype.hasOwnProperty.call(castExtreme, fragmentRollType))
    castExtreme[fragmentRollType] = resultModifiers.casterExtreme;
  // EXPLODING DAMAGE DICE (damage_type_roll.php ~L553): a Damage Roll fragment that is not forced to an extreme, under the campaign's
  // exploding_damage_dice house rule — every kept die seeds a chain on the ROLLER's Fortune (R2 ask 2).
  const explodeRule = !damageOverTime && houseRules?.explodingDice === true && resultModifiers.extreme === null;
  const explode = explodeRule && primaryTypeMax === null; // a fragment forced to an extreme never explodes
  // the roller's Fortune — read for every fragment the rule reaches (an extra fragment explodes on its own type even when the primary's is maxed)
  const explodeFortune = explodeRule ? Math.trunc(Number(scalar(ctx.read.value(caster, 'fortune')) ?? 0)) || 0 : 0;
  // attacks_ignore_natural_damage_mitigation (Psion Metamorph "Living Arsenal", 2026-10-07 — PHP ft_natural_damage_mitigation, R-180): an
  // ATTACK-roll fragment (a weapon template hit or a spell attack roll — PHP's isset($roll_result_hit)) skips the target's NATURAL Damage
  // Mitigation (its base creature definition's DM). Settled on state, before any draw: the base definition must be held.
  let naturalMitigation = 0;
  if (attackHit && !ignoresMitigation && ctx.read.has(target, 'damage_mitigation')
      && providerFlag(ATTACKS_IGNORE_NATURAL_DAMAGE_MITIGATION)) {
    const currentMitigation = Math.trunc(Number(scalar(ctx.read.value(target, 'damage_mitigation'))));
    if (Number.isFinite(currentMitigation) && currentMitigation > 0) {
      const natural = naturalDamageMitigation(target, currentMitigation, (id) => ctx.read.definition(id), (row, key) => ctx.read.value(row, key));
      if (natural === null) handBack('damage_type_roll:natural-mitigation-base-definition-not-held');
      naturalMitigation = natural;
    }
  }
  // max_damage_vs_creature_type.php returns null when this target's single
  // creature_type is absent from the fragment's whitelist. The maxed fragment
  // itself remains a hand-back until its forced-extreme face log is modeled.
  if (ctx.read.has(ability, 'maximum_damage_against_creature_type')) {
    const rawType = ctx.read.value(target, 'creature_type');
    const targetType = upper(typeof rawType === 'string' ? rawType : itemField(record(rawType), 'item_id'));
    const maximumTypes = ctx.read.entries(ability, 'maximum_damage_against_creature_type').map((entry) =>
      upper(typeof entry === 'string' ? entry : itemField(record(entry), 'item_id')));
    if (targetType && maximumTypes.includes(targetType))
      handBack('damage_type_roll:ability:maximum_damage_against_creature_type');
  }
  // max_damage_vs_creature_type.php:92–171 has two MORE providers than the fragment's own layer: entries attached to the WEAPON this
  // strike is made with (passive_ability / ability buckets), and caster passive entries whose `source` binds them to equipped gear
  // (an enchantment part such as a creature-slayer rider). Not modelled ⇒ hand back whenever such a provider could carry the layer
  // (review 2026-09-26); an unresolvable provider hands back too.
  maxDamageProviderGuard(ctx, ability, caster);
  for (const key of ['minimum_damage_against_creature_type',
    'damage_damages_caster', 'damage_type_roll_adv_dis_stacks',
    'damage_type_dice_quantity_mod', 'extra_damage_type', 'hit_to_crit',
    'effect_ignores_damage_type_immunity'])
    if (ctx.read.has(ability, key)) handBack(`damage_type_roll:ability:${key}`);
  const casterPassives = record(record(record(caster.computed)?.creature)?.abilities)?.passive_ability;
  if (casterPassives != null && !Array.isArray(casterPassives)) handBack('damage_type_roll:passive-carriers-not-held');
  const abilityRollType = upper(ctx.read.value(ability, 'roll_type'));
  // extra_damage_requires_damage_type (Overheat, 2026-10-07; PHP ft_extra_damage_damage_type_gate_allows — R-146): an extra_damage_roll
  // rider listing damage types fires only when the item being used deals one of them (a weapon hit with no damage_type of its own deals
  // the caster's main_weapon_damage_type — the primary fragment's own fallback below).
  const damageTypeIdsOf = (row: Row, key: string): string[] => ctx.read.entries(row, key).map((entry) =>
    upper(typeof entry === 'string' ? entry : record(record(entry)?.items)?.item_id ?? record(entry)?.item_id)).filter(Boolean);
  const hostDamageTypes = ((): string[] => {
    const own = damageTypeIdsOf(ability, 'damage_type');
    if (own.length || !weaponHit) return own;
    const main = upper(scalar(ctx.read.value(caster, 'main_weapon_damage_type')));
    return main ? [main] : [];
  })();
  const extraTypeGateAllows = (def: Row): boolean =>
    extraDamageTypeGateAllows(damageTypeIdsOf(def, EXTRA_DAMAGE_REQUIRES_DAMAGE_TYPE), hostDamageTypes);
  // CASTER extra_damage_roll passives (damage_type_rolls.php ~L174-320; lane A5, 2026-10-01 — playtest 09-30 finding #6: the legacy loop read
  // a missing item_arr and silently skipped them; the browser rolls them). Settled HERE, pre-draw; rolled after the primary fragment below.
  // PHP iterates layer_entries($caster_arr, 'passive_ability') then ft_status_item_passive_entries() (a passive ITEM applied to the caster
  // as a status — status-item-passive-entries.ts) in order, no dedup among the bucket: each active entry whose own
  // roll_type is extra_damage_roll fires when it carries NO trigger key (contains_trigger_key) or — triggered — when its
  // triggers_when_caster_makes_roll_type lists this ability's roll type and it authors no hit / crit / advantage gate (case 2). A DOT tick
  // and an attack-ability phase roll no caster extras. Plain weapon hits share these extra_damage_roll fragments;
  // gated triggers and extras absent from the root list retain named refusals.
  // Weapon hits also execute untriggered Extra Weapon Damage through this same loop.
  const casterExtras: Array<{ definition: Row; triggered: boolean; id: string }> = [];
  let casterExtraTypes: string[] = [];
  {
    const triggerKey = (def: Row): boolean => Object.keys(record(def.item_layer_types) ?? {}).some((key) => key.includes('trigger'))
      || Object.keys(def).some((key) => key.includes('trigger'));
    // Wizard Mastery port (2026-10-09): a caster extra scoped by if_circle_of_magic_cast / _not_cast (Thoughtful Annihilation — Evocation
    // L20, "+Mind to Evocation spell damage") fires only on a cast of a listed circle; a non-spell use cast no circle. BROWSER RULE: PHP's
    // loop never read the predicate.
    const castCircles = String(itemField(ability, 'item_type') ?? '') === 'spell' ? spellCircles(ctx.read, ability) : [];
    const firesFor = (def: Row): 'untriggered' | 'triggered' | null => {
      const rollType = upper(ctx.read.value(def, 'roll_type'));
      // Selected weapon/ammo grants share this fragment executor in the numeric
      // and object attack callers, just as they do in the explicit weapon lane.
      if (rollType !== EXTRA_DAMAGE_ROLL
          && !(weaponHit && rollType === '42A38751-5549-CDE7-528E-AED309A2B07D' && !triggerKey(def))) return null;
      if (!extraTypeGateAllows(def)) return null;
      if (!circlePredicateMatch(ctx.read, castCircles, def)) return null;
      if (!triggerKey(def)) return 'untriggered';
      const triggers = ctx.read.entries(def, 'triggers_when_caster_makes_roll_type').map((entry) =>
        upper(typeof entry === 'string' ? entry : itemField(record(entry), 'item_id') ?? record(entry)?.item_id));
      return triggers.includes(abilityRollType) ? 'triggered' : null;
    };
    const skipExtras = damageOverTime || ['attack_ability', 'opportunity_attack_ability'].includes(String(itemField(ability, 'item_type') ?? ''));
    const admit = (definition: Row, id: string): void => {
      const fires = firesFor(definition);
      if (!fires || skipExtras) return;
      if (fires === 'triggered' && ['triggers_if_roll_type_is_at_adv', 'triggers_on_hit', 'triggers_on_critical_hit'].some((key) => ctx.read.has(definition, key)))
        handBack('damage_type_roll:unmodelled:caster-extra-damage-passive:gated-trigger');
      casterExtras.push({ definition, triggered: fires === 'triggered', id: upper(itemField(definition, 'item_id') ?? id) });
    };
    const rootIds = new Set<string>();
    for (const passive of ctx.read.entries(caster, 'passive_ability')) {
      const row = record(passive);
      const id = abilityEntryId(passive);
      if (id) rootIds.add(id);
      if (!passiveEntryActive(passive)) continue; // passive_ability_entry_is_active (truthiness — the status walk's mask uses the same)
      const inline = record(row?.item_arr) ?? row;
      const definition = inline && record(inline.item_layer_types) ? inline : ctx.read.definition(id);
      if (!definition) passiveNotHeld(id);
      admit(definition!, id);
    }
    if (!skipExtras) {
      // the passive ITEMS applied to the caster as statuses, after the bucket (ft_status_item_passive_entries — dedup, team, conditions)
      const statusPassives = statusItemPassiveEntries(caster, casterStatusRows.map((status, i) => ({ status, definition: casterStatusDefs[i] ?? null })),
        { value: (row, key) => ctx.read.value(row, key), entries: (row, key) => ctx.read.entries(row, key),
          definition: (id) => ctx.read.definition(id), member: (id) => ctx.members.get(id)
            ?? Object.values((ctx.encounter.encounter_creatures_arr ?? {}) as object).map(record).find((row) => ecId(row) === id) ?? undefined,
          conditionScene: () => ctx.read.possessorScene?.(caster, [...ctx.members.values()])?.conditionScene?.()
            ?? { encounter: ctx.encounter, campaign: undefined },
          relevant: (def) => firesFor(def) !== null });
      if ('gap' in statusPassives) {
        const id = /:([0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12})$/.exec(statusPassives.gap)?.[1];
        if (statusPassives.gap.startsWith('status-passive-definition-not-held:') && id) passiveNotHeld(id);
        handBack(`damage_type_roll:caster-${statusPassives.gap}`);
      }
      for (const { definition, id } of statusPassives.entries) admit(definition, id);
      // every passive ITEM status is judged by that walk, whatever its verdict; the COMPILED list may carry its folded copy
      const statusItemIds = new Set(casterStatusDefs.filter((def) => String(itemField(def, 'item_type') ?? '') === 'passive_ability')
        .map((def) => upper(itemField(def, 'item_id'))));
      const compiled = Array.isArray(casterPassives) ? casterPassives : [];
      // A compiled / status-nested extra must also be held in the root grant list.
      for (const passive of [...compiled, ...casterStatusDefs.flatMap((def) => ctx.read.entries(def, 'passive_ability'))]) {
        const row = record(passive);
        if (row?.passive_conditions_active === false) continue;
        const id = abilityEntryId(passive);
        if (id && (rootIds.has(id) || (statusItemIds.has(id) && compiled.includes(passive)))) continue;
        const inline = record(row?.item_arr) ?? row;
        const definition = inline && record(inline.item_layer_types) ? inline : ctx.read.definition(id);
        if (!definition) passiveNotHeld(id);
        if (firesFor(definition!)) handBack('damage_type_roll:unmodelled:caster-extra-damage-passive:not-in-root-list');
      }
    }
  }
  casterExtraTypes = [...new Set(casterExtras.map((extra) => extraFragmentType(ctx, extra.definition, caster).id).filter(Boolean))];
  let vitality = record(record(target.computed)?.vitality);
  // An OBJECT target (target_arr_is_inventory_target — R2 asks 3–4): an encounter inventory row (`inventory.inventory_id`, no
  // encounter_creatures identity), or a caller's creature-shaped shim of one whose encounter_creature_id IS its inventory_id. The
  // caller hydrates computed.vitality (v_current / v_max / effective_v_max; a lean bucket elides a zero v_temp). With the
  // ctx.objectDamage capability the object's own application runs (objectDamageApply); a shim without it takes the creature
  // application (its caller reads the verdict back itself); a bare inventory row without it hands back.
  if (objectTarget && ctx.objectDamage === undefined && !ecId(target)) handBack('apply_damage_to_target:object-target-not-modeled');
  if (objectTarget && (!vitality || vitality.v_current === undefined || vitality.v_max === undefined)) {
    vitality = resolveObjectVitality(target, ctx.encounter, ctx.read.definition);
    if (vitality) installObjectVitality(target, vitality);
  }
  if (objectTarget && !vitality) throw new InventoryDamageFactsNeeded([{ kind: 'vitality', id: inventoryId(target) }]);
  // A hosted / caster extra fragment's static gaps — every named hand-back rollExtraFragment would meet, asked BEFORE any die for the
  // caster extras (Codex review 2026-10-10: Thoughtful Annihilation's checks surfaced after the primary's dice) and again per fragment.
  const objectMultiplier = objectTarget ? objectDamageMultiplier(ctx, caster, ability, providers) : { factor: 1, source: '' };
  const extraFragmentGaps = (extra: Row, casterTriggered: boolean): void => {
    rollTypeSourceStacks(ctx, extra, caster, target, upper(ctx.read.value(extra, 'roll_type')) === DOT_ROLL ? DOT_ROLL : DAMAGE_ROLL,
      upper(itemField(extra, 'item_id')));
    // PHP resolves each hosted extra through its own damage_type_roll(): the exploding-dice pass (rolled per fragment in rollExtraFragment),
    // the forced extreme and the doubled / halved scaling apply per fragment. The scaling is not modelled per extra (Codex A16 review) → named.
    if (resultModifiers.extreme !== null || doubleSources.standing || doubleSources.active || resultModifiers.casterHalve
        || resultModifiers.targetDouble || resultModifiers.targetHalve)
      handBack('damage_type_roll:unmodelled:hosted-extra-result-modifier');
    for (const key of ['maximum_damage_against_creature_type', 'minimum_damage_against_creature_type',
      'damage_damages_caster', 'damage_type_roll_adv_dis_stacks',
      'damage_type_dice_quantity_mod', 'extra_damage_type', 'hit_to_crit',
      'effect_ignores_damage_type_immunity'])
      if (ctx.read.has(extra, key)) handBack(`damage_type_roll:hosted-extra:${key}`);
    if (!casterTriggered && (Object.keys(record(extra.item_layer_types) ?? {}).some((key) => key.startsWith('triggers_'))
        || ['triggers_on_next_roll_type', 'triggers_when_caster_makes_roll_type', 'triggers_when_target_takes_damage'].some((key) => ctx.read.has(extra, key))))
      handBack('damage_type_roll:unmodelled:triggered-hosted-extra');
    // check_post_damage_type_roll_conditions.php runs again for THIS fragment (damage_type_rolls.php calls
    // damage_type_roll() once per hosted extra, with $roll_ability_arr = the extra's own item_arr) — its
    // size/creature-type/division gates are not ported; hand back rather than silently ignore a live gate.
    for (const key of ['ability_only_works_against_size_category_number_above', 'ability_only_works_against_size_category_number_below',
      'ability_works_only_against_creature_type', 'ability_does_not_work_against_creature_type',
      'ability_works_only_against_creature_sub_type', 'ability_does_not_work_against_creature_sub_type',
      'ability_works_only_against_creature_lineage', 'ability_works_only_against_creature_ancestry', 'ability_works_only_against_creature_grouping',
      'ability_does_not_work_against_creature_lineage', 'ability_does_not_work_against_creature_ancestry', 'ability_does_not_work_against_creature_grouping'])
      if (ctx.read.has(extra, key)) handBack(`damage_type_roll:hosted-extra-gate:${key}`);
    // damage_type_roll.php ~L66–96 for this fragment's own row: dice / die as authored (a dice-less rider rolls nothing — Thoughtful
    // Annihilation, dice 0 / die 0), its mod through discern_variable_tags (an essence tag reads the CASTER's final essence — the
    // layer_value bridge; the primary's [body] twin), added once to this fragment.
    const extraDice = nextSpellDamageDice(ability, requiredInt(damageFormulaDice(ctx, input, caster, ctx.read.value(extra, 'dice') ?? 0), 'damage_type_roll:hosted-extra-variable-dice-or-mod'));
    const extraDie = requiredInt(ctx.read.value(extra, 'die') ?? 0, 'damage_type_roll:hosted-extra-variable-dice-or-mod');
    const extraMod = fragmentMod(ctx, ctx.read.value(extra, 'mod'), caster);
    if (extraDice < 0 || extraDice > 100 || extraDie < 0 || extraDie > 1000 || (extraDice > 0 && extraDie < 1))
      handBack('damage_type_roll:unmodelled:hosted-extra-dice-shape');
    // upcast_roll() runs per fragment on the CAST item's layers (damage_type_roll.php ~L104): a cast above the spell's level whose
    // dice_upcast / mod_upcast would grow THIS fragment is not modelled (step 5a) → named; a level-variable cast it leaves unchanged is served
    if (ctx.castLevel != null) {
      const grown = upcastRoll(ctx, ctx.read.definition(upper(input.abilityId)) ?? ability, extraDice, extraMod);
      if (grown.dice !== extraDice || grown.mod !== extraMod) handBack('damage_type_roll:upcast-hosted-extra');
    }
    // ~L476–500: a slot-2 amount pick wins, else the fragment's own type, main weapon type, then Bludgeoning.
    // Existing slot-1 retyping of hosted riders keeps its named refusal.
    const { id: extraType } = extraPick ? { id: extraPick } : extraFragmentType(ctx, extra, caster);
    if (!extraPick && [input.useAbilityVariable1, input.useAbilityVariable2].some((v) => /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i.test(String(v ?? '').trim())
        && String(itemField(ctx.read.definition(upper(v)), 'item_type') ?? '') === 'damage_type'))
      handBack('damage_type_roll:hosted-extra-damage-type-pick');
    const extraTypeDefinition = ctx.read.definition(extraType);
    // the id rides the reason so the page warms the damage-type definition and resolves again (ability-truth.ts `…definition-not-held:<id>`;
    // Wizard v9 C5: Corpse Explosion's hosted Necrotic extra stopped here on a page that never hydrated Necrotic)
    if (!extraTypeDefinition) handBack(`damage_type_roll:hosted-extra-type-definition-not-held:${extraType}`);
  };
  for (const extra of casterExtras) extraFragmentGaps(extra.definition, extra.triggered);
  if (attackAbility) extraFragmentGaps(attackAbility, false);
  let tempBefore = objectTarget ? requiredInt(vitality?.v_temp ?? 0, 'damage_type_roll:temp-vitality-not-held')
    : requiredInt(vitality?.v_temp ?? ecLean(target, 'v_temp'), 'damage_type_roll:temp-vitality-not-held');
  // By-id damage branches (R2 ask 7) and the damage fan-out's janus_trigger carriers (apply_damage_to_target_inc.php ~L770–876): a
  // creature target's triggers_when_caster_takes_{any_damage,damage_type} and a member attacker's triggers_when_target_takes_* dispatch
  // in-pipeline (damageTakenTriggers) when every carrier is one the executor runs (triggerFamilyPreDraw). An OBJECT target (no reaction
  // lane; its attacker-side families incite from an inventory row) and a non-member attacker row still hand back, before a die is seen.
  for (const status of activeStatuses(ctx, target))
    if (DAMAGE_BRANCH_TARGET_STATUS_IDS.has(upper(status.status_item_id)))
      handBack(`apply_damage_to_target:status-branch:${upper(status.status_item_id)}`);
  // R-113: damage wakes a sleeper — every hand-back decided here, on state (an object target never sleeps)
  const wake = objectTarget ? null : planWake(ctx, target);
  // R-150 (2026-10-07): a status carrying target_retries_contesting_roll_on_damage_taken re-rolls its contest — planned here, on state
  const retry = objectTarget ? null : planContestRetry(ctx, caster, target);
  // the ordinary Charmed / Dominated damage break (attempt_to_break_charm_effects_if_damage_taken) — planned here, on state
  const charm = objectTarget ? null : planCharmBreak(ctx, target);
  {
    // + the caster's extra_damage_roll fragments (lane A5): their types are rolled types too (Codex A5 review 2 P2)
    const damageTypes = [...new Set([...possibleFragmentTypes(ctx, ability, types[0] ?? '', caster), ...casterExtraTypes,
      ...(attackAbility ? [extraPick ?? extraFragmentType(ctx, attackAbility, caster).id] : [])])];
    if (objectTarget) {
      if (carrierAuthorsAny(ctx, target, [TAKES_ANY_DAMAGE, TAKES_DAMAGE_TYPE], 'apply_damage_to_target:takes-damage-trigger'))
        handBack('janus_trigger:unmodelled:takes-damage-trigger');
    } else {
      const gap = isInventoryRow(caster) && !ctx.members.has(upper(input.casterId)) ? objectAttackerGap : undefined;
      const inciting = ecId(caster) ? ctx.members.get(ecId(caster)) ?? null : null;
      triggerFamilyPreDraw(ctx, target, { trigger: TAKES_ANY_DAMAGE, damageTypes }, gap, inciting);
      triggerFamilyPreDraw(ctx, target, { trigger: TAKES_DAMAGE_TYPE, damageTypes, criticalHit: critical }, gap, inciting);
    }
    const attackerMember = ecId(caster) ? ctx.members.get(ecId(caster)) : undefined;
    if (ecId(caster) && (objectTarget || !attackerMember)) {
      if (carrierAuthorsAny(ctx, caster, [TARGET_TAKES_ANY_DAMAGE, TARGET_TAKES_DAMAGE_TYPE], 'apply_damage_to_target:target-takes-damage-trigger'))
        handBack('janus_trigger:unmodelled:target-takes-damage-trigger');
    } else if (attackerMember) {
      triggerFamilyPreDraw(ctx, attackerMember, { trigger: TARGET_TAKES_ANY_DAMAGE, damageTypes }, undefined, target);
      triggerFamilyPreDraw(ctx, attackerMember, { trigger: TARGET_TAKES_DAMAGE_TYPE, damageTypes, criticalHit: critical }, undefined, target);
    }
  }
  requireTemporaryVitalityLane(ctx, target, tempBefore);
  // check_concentration.php (end of apply_damage_to_target): the FIRST Concentrating row, settled on state before any draw.
  let concentrating: Row | null = null;
  for (const status of ctx.read.statuses(target)) {
    const definition = ctx.read.definition(String(status.status_item_id ?? '')) ?? record(status.status_arr);
    if (!definition) handBack('calc_total_damage_and_apply_to_target:unmodelled:status-definition');
    const name = String(record(definition.items)?.item_name ?? '').toLowerCase();
    if (upper(status.status_item_id) === CONCENTRATING) {
      // Death cleanup can retire this source; an actual survivor still needs the concentration capability below.
      if (ctx.concentrationBreaks === undefined && !ctx.read.deathResources)
        handBack('calc_total_damage_and_apply_to_target:unmodelled:concentration-check');
      if (!/^[0-9A-F]{8}(-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(String(status.status_id ?? '')))
        handBack('check_concentration:status-id-not-held');
      concentrating ??= status;
    }
    // Status typed defenses are already folded into computed.creature.defenses; defendFragment applies each family once.
    for (const key of ['damage_mitigation',
      // calc_total's warding transfer and the other untyped target modifiers, status-carried (R2 oracle 2026-09-28).
      'transfer_damage_percentage_to_caster_of_status_effect', 'all_incoming_damage_changed_by_percentage', 'damage_reduction',
      'damage_taken_pct_mod', 'reduce_damage_roll', 'warding_bond', 'damage_redirect', 'object_damage_mitigation',
      'object_damage_type_resistance', 'object_damage_type_immunity', 'object_damage_type_vulnerability', 'item_composition',
      'triggers_when_target_takes_damage', 'charmed',
      'transform_into_creature_on_self_zero_vitality'])
      if (ctx.read.has(definition, key)
          && !(key === 'transform_into_creature_on_self_zero_vitality' && ctx.read.spawnTransform)
          && !(key === 'damage_mitigation' && compiledMitigationSource(target, heldStatusItemId(status))))
        handBack(`calc_total_damage_and_apply_to_target:unmodelled:status.${key}`);
  }
  targetDamageReductions(ctx, target); // Validate held and same-use reduction dice before preparing the fragment.
  for (const passive of damageTargetPassiveEntries(ctx, target)) {
    const passiveRow = record(passive);
    if (!passiveEntryActive(passive)) continue;
    // A compiled PC's bucket entry carries a LAYERLESS identity shell under item_arr — read the catalog definition instead, or every
    // key below reads absent and the page resolves damage PHP would resist (2026-10-07: Totemic Nullflow / Resist the Void).
    const nestedDefinition = record(passiveRow?.item_arr);
    const passiveId = abilityEntryId(passive);
    if (passiveId === SPELL_DAMAGE_VULNERABILITY_PASSIVE) continue; // Canonical flag-only passive; the resolver accepts its lean reference.
    const definition = (nestedDefinition && record(nestedDefinition.item_layer_types) ? nestedDefinition : null)
      ?? ctx.read.definition(passiveId);
    if (!definition) passiveNotHeld(passiveId);
    // Damage-taken listeners belong to triggerFamilyPreDraw / damageTakenTriggers above, including compiled bucket passives.
    // Typed defences are already compiled into computed.creature.defenses, which defendFragment reads.
    if (ctx.read.has(definition, 'extra_damage_taken_roll')) handBack('calc_total_damage_and_apply_to_target:unmodelled:passive.extra_damage_taken_roll');
  }
  // [x] retains its amount-picker semantics; other authored tags read the held caster through the shared formula resolver.
  const authoredDice = ctx.read.value(ability, 'dice');
  const rawDice = damageFormulaDice(ctx, input, caster, authoredDice);
  const versatile = !!attackAbility && Number(ctx.read.value(caster, 'count_both_hands_weapon_equipped')) === 1
    && ctx.read.has(ability, 'versatile_dice');
  const rawDie = ctx.read.value(ability, versatile ? 'versatile_die' : 'die');
  const rawMod = ctx.read.value(ability, 'mod');
  if (!/^\d+$/.test(String(rawDice)) || !/^\d+$/.test(String(rawDie)) ||
    (rawMod != null && !/^-?\d+$/.test(String(rawMod)) && rawMod !== '[body]'))
    handBack('damage_type_roll:variable-dice');
  let dice = checkScalableCantrip(ctx, ability, caster, requiredInt(rawDice, 'damage_type_roll:invalid-dice'));
  const die = requiredInt(rawDie, 'damage_type_roll:invalid-die');
  if (ctx.read.has(ability, 'mod') && rawMod == null) handBack('damage_type_roll:invalid-mod');
  let mod = rawMod == null || rawMod === '[body]' ? 0 : requiredInt(rawMod, 'damage_type_roll:invalid-mod');
  if (rawMod === '[body]') {
    const body = ctx.read.value(caster, 'body');
    if (body == null) handBack('damage_type_roll:body-tag-not-held');
    mod = requiredInt(body, 'damage_type_roll:invalid-body');
  }
  // upcast_roll() (damage_type_roll.php ~L104): the CAST item's dice_upcast / mod_upcast × the levels above its own, after the fragment's
  // own dice / mod. A mod the upcast grows has no face tuple here yet → PHP (Session 1 step 5a).
  if (ctx.castLevel != null) {
    const castItem = ctx.read.definition(upper(input.abilityId)) ?? ability;
    const up = upcastRoll(ctx, castItem, dice, mod);
    if (up.mod !== mod) handBack('damage_type_roll:upcast-mod-face');
    dice = up.dice;
  }
  dice = nextSpellDamageDice(ability, dice);
  const abilityMod = mod;
  let weaponBodyMod = 0;
  if (weaponHit) {
    if (ctx.read.has(ability, 'item_off_hand') || (!attackAbility && (ctx.read.has(ability, 'versatile_dice')
        || ctx.read.has(caster, 'count_both_hands_weapon_equipped'))))
      handBack('damage_type_roll:unmodelled:weapon-hit-modifiers');
    if (versatile)
      dice = requiredInt(ctx.read.value(ability, 'versatile_dice'), 'damage_type_roll:invalid-dice');
    // damage_type_roll_applies_weapon_body_mod_to_roll: ranged Body comes from a throwable or Precision Shots,
    // including the feat's own layer and nested grants (the same provider walk as the ordinary weapon lane).
    const rollType = upper(ctx.read.value(ability, 'roll_type'));
    let addsBody = rollType === WEAPON_MELEE_ATTACK_ROLL || rollType === WEAPON_RANGED_ATTACK_ROLL && ctx.read.has(ability, 'item_throwable');
    if (rollType === WEAPON_RANGED_ATTACK_ROLL && !addsBody) {
      const provider = casterLayerProvider(ctxProviderReaders(ctx), caster, 'precision_shots');
      if (provider === undefined) handBack('weapon:precision-shots-provider-unavailable');
      addsBody = provider !== null;
    }
    if (addsBody) {
      const body = ctx.read.value(caster, 'body');
      if (body == null) handBack('damage_type_roll:weapon-body-not-held');
      weaponBodyMod = requiredInt(body, 'damage_type_roll:invalid-body');
      mod += weaponBodyMod;
    }
  }
  if (dice < 0 || dice > 100 || die < 1 || die > 1000) handBack('damage_type_roll:dice-shape');
  const rolled = rollDiceFaces(ctx, dice, die, advDisStacks);
  const damageTypeDefinition = ctx.read.definition(types[0]);
  if (!damageTypeDefinition) definitionNotHeld('damage_type_roll:damage-type-definition-not-held', types[0]);
  const damageTypeName = String(itemField(damageTypeDefinition, 'item_name') ?? 'Unknown');
  const sourceId = itemField(ability, 'item_id');
  const sourceName = itemField(ability, 'item_name');
  const sourceType = String(itemField(ability, 'item_type') ?? '');
  const sourceKind = ['weapon', 'natural_weapon'].includes(sourceType) ? 'weapon'
    : ['attack_ability', 'opportunity_attack_ability', 'ability', 'full_ability', 'quick_ability', 'free_ability', 'reaction_ability'].includes(sourceType) ? 'ability'
    : sourceType;
  const sourceMeta: Record<string, unknown> = { damage_type_id: types[0] };
  const composition = ctx.read.value(ability, 'item_composition');
  if (composition != null) sourceMeta.item_composition = composition;
  if (sourceId) sourceMeta.source_item_id = sourceId;
  if (sourceName) sourceMeta.source_name = sourceName;
  if (sourceType) sourceMeta.source_item_type = sourceType;
  if (sourceKind) sourceMeta.source_kind = sourceKind;
  const fullFaces: unknown[] = rolled.faces.map(([value, label, meta]) =>
    [value, `${label} | ${damageTypeName}`, { ...meta, ...sourceMeta }]);
  let fragmentResult = rolled.total + mod;
  if (explode) {
    const exploded = explodeFragmentDice(ctx, rolled.faces, die, explodeFortune, sourceMeta);
    fullFaces.push(...exploded.tuples);
    fragmentResult += exploded.total;
  }
  if (resultModifiers.extreme === 'max') {
    fragmentResult = dice * die + mod;
    fullFaces.push(['Ω', 'Roll Type is Rolled At Max', sourceMeta]);
  } else if (primaryTypeMax !== null) {
    fragmentResult = dice * die + mod;
    fullFaces.push(['Ω', `Damage Type is Rolled At Max — ${primaryTypeMax}`, sourceMeta]);
  } else if (resultModifiers.extreme === 'min') {
    fragmentResult = dice + mod;
    fullFaces.push(['α', 'Roll Type is Rolled At Min', sourceMeta]);
  }
  // check_critical_hit_logic.php (damage_type_roll.php:840): after the dice, BEFORE the modifier / essence tuples — the
  // campaign's crit model on this fragment (Doubled reads the result as it stands, modifier and essence included).
  let critBonus = 0;
  const critMultiplier = criticalHitDamageMultiplier(ctx.read, caster, ability);
  if (critical) {
    // Crit: Rolled dice detonate when the fragment can explode (crit_variants.php: $fragment_can_explode — a Damage Roll not forced
    // to an extreme — AND the house rule), on the roller's Fortune; each explosion tuple carries PHP's explosion meta.
    const bonus = criticalHitBonus(ctx.roll, houseRules!.critVariant, { dice, die, rollResult: fragmentResult },
      { canExplode: explode, fortune: explodeFortune, multiplier: critMultiplier });
    critBonus = bonus.total;
    if (bonus.tuples.length) for (const tuple of bonus.tuples) fullFaces.push([tuple.value, tuple.label, tuple.kind === 'explosion'
      ? { ...sourceMeta, kind: 'explosion', explosion_link: tuple.link, explosion_threshold: tuple.threshold, explosion_die: die }
      : { ...sourceMeta, kind: 'critical' }]);
    else fullFaces.push([bonus.total, bonus.variant === 'doubled' ? 'Critical Damage (Doubled)' : 'Critical Damage', { ...sourceMeta, kind: 'critical' }]);
    // THE CRIT CASCADES (check_critical_hit_logic.php ~L231–364; lane A7, 2026-10-02 — the playtest campaign runs them, so every attack there
    // handed back): on the use's FIRST damage fragment (this primary — the extra fragments crit too, but never cascade), under the
    // campaign's Remarkable rule a d20 is rolled and logged as its own row (roll_dc 20, the rule's banner) BEFORE the damage row; a 20 adds
    // the fragment's dice × die again (pinned at maximum under every crit model) and, under World-Shaking, rolls the next d20 the same way.
    if (houseRules!.remarkable) {
      const cascade = (rule: 'remarkable' | 'worldShaking', houseRule: CritHouseRule): boolean => {
        const meta = CRIT_CASCADE_RULES[rule];
        const face = ctx.roll.d(20);
        insertRoll(ctx, { roll_type_1: meta.rollType, roll_type_2: '', roll_item_id: meta.id, by: caster, to: target, roll_result: face,
          rolls_full: [[face, 'd20_1']], dice: 1, die: 20, mod: 0, adv_dis_stacks: 0, dc: 20, house_rule: houseRule });
        if (face !== 20) return false;
        critBonus += dice * die;
        fullFaces.push([dice * die, meta.bonusLabel, { ...sourceMeta, kind: 'critical' }]);
        return true;
      };
      if (cascade('remarkable', 'critical_hits_improved_damage_remarkable') && houseRules!.worldShaking)
        cascade('worldShaking', 'critical_hits_improved_damage_world_shaking');
    }
  }
  fragmentResult += critBonus;
  const scale = (kind: 'double' | 'halve', source: string): void => {
    fragmentResult = kind === 'double' ? fragmentResult * 2 : Math.floor(fragmentResult / 2);
    fullFaces.push([kind === 'double' ? 'Γ' : 'ξ',
      `Roll Type Roll Result is ${kind === 'double' ? 'Doubled' : 'Halved'} — ${source}`, sourceMeta]);
  };
  if (doubleSources.standing) scale('double', doubleSources.standing);
  if (doubleSources.active) scale('double', doubleSources.active);
  // damage_to_objects_multiplier — after the caster doubling, before the halving / typed defences (damage_type_roll.php, R-155)
  if (objectMultiplier.factor > 1) {
    fragmentResult *= objectMultiplier.factor;
    fullFaces.push([`×${objectMultiplier.factor}`, `Damage to Objects x${objectMultiplier.factor} — ${objectMultiplier.source}`, sourceMeta]);
  }
  // scroll_spell_damage_multiplier (Wizard v9 WP5, charged-cast.ts — browser-only): a SPELL cast from a scroll, the same per-fragment seam
  // (after the dice / crit / caster doubling, before the halving and typed defences); every extra fragment below takes it too.
  const scrollMultiplier = scrollSpellDamageMultiplier(ctx, caster, ability);
  if (scrollMultiplier.factor > 1) {
    fragmentResult *= scrollMultiplier.factor;
    fullFaces.push([`×${scrollMultiplier.factor}`, `Scroll Spell Damage x${scrollMultiplier.factor} — ${scrollMultiplier.source}`, sourceMeta]);
  }
  if (resultModifiers.casterHalve) scale('halve', resultModifiers.casterHalve);
  if (resultModifiers.targetDouble) scale('double', resultModifiers.targetDouble);
  if (resultModifiers.targetHalve) scale('halve', resultModifiers.targetHalve);
  if (abilityMod) fullFaces.push([abilityMod, String(sourceName ?? 'Damage Mod'), { ...sourceMeta, kind: 'damage_mod' }]);
  if (weaponBodyMod)
    fullFaces.push([weaponBodyMod, 'body Essence', { ...sourceMeta, kind: 'essence', essence_name: 'body' }]);
  // The PRIMARY fragment's typed defences (damage_type_roll.php:1070): its tuples follow its dice / mod / essence. The fragment
  // then floors at 0 (damage_type_roll.php:1093) — after check_post's half (which, with no hosted extra, the total below applies).
  // a damage-type PICK set types[0] above (damage_type_roll.php ~L476): it is this fragment's raw type too
  const rawPrimaryType = pick ?? String(scalar(weaponHit && !ctx.read.entries(ability, 'damage_type').length
    ? ctx.read.value(caster, 'main_weapon_damage_type')
    : (() => { const first = ctx.read.entries(ability, 'damage_type')[0]; return typeof first === 'string' ? first : record(record(first)?.items)?.item_id ?? record(first)?.item_id; })()) ?? '');
  if (upper(rawPrimaryType) !== types[0]) handBack('damage_type_roll:primary-type-not-held');
  // calc_total's roll_feedback reads each fragment's feedback_rolled (the result before its defences) and zero reason.
  const primaryFeedbackRolled = Math.max(0, Math.trunc(fragmentResult));
  const primaryZero = { reason: null as string | null };
  const outer = ctx.read.definition(upper(input.abilityId)) ?? ability;
  const primaryTyped = defendFragment(ctx, target, rawPrimaryType, damageTypeName, fragmentResult, fullFaces, primaryZero, casterIgnoresResistance, ability);
  const primaryResult = defendSpellFragment(ctx, target, outer, sourceMeta, primaryTyped, fullFaces, primaryZero);
  let hostedExtraContributed = false;
  // ft_damage_types_in_roll(): every fragment that rolled — the primary's selected type, then each executed hosted extra's.
  const rolledFragmentTypes: string[] = [types[0]!];
  // check_post_damage_type_roll_conditions.php runs at the tail of EACH fragment's damage_type_roll(): the contest-half
  // (contesting_roll_successful_for_half_damage on the ACTIVE item) halves THIS fragment — floor, its own '0.5 Λ' tuple after its
  // defences — and the fragment then floors at 0. The primary first; each hosted extra below the same (Session A step 4b, 2026-09-30).
  if (half) fullFaces.push(['0.5 Λ', 'Half Damage']);
  let fullResult = Math.max(0, half ? Math.floor(primaryResult / 2) : primaryResult);
  let casterHealing = ctx.read.has(ability, 'damage_heals_caster') ? fullResult : 0;
  let exemptMitigationDamage = exemptMitigationTypes.has(upper(rawPrimaryType)) ? fullResult : 0;
  // ONE EXTRA FRAGMENT (damage_type_rolls.php → damage_type_roll() with $roll_ability_arr = the extra's own definition): its own dice / mod /
  // type / typed defences / contest-half / status gates, accumulated into this row. Shared by the CASTER's extra_damage_roll passives and the
  // riders HOSTED on the ability (lane A5, 2026-10-01 — was the hosted loop's body). `casterTriggered`: a caster passive whose trigger keys
  // were already consumed as its firing condition (triggers_when_caster_makes_roll_type).
  const rollExtraFragment = (extra: Row, casterTriggered: boolean): void => {
    extraFragmentGaps(extra, casterTriggered);
    const extraDice = nextSpellDamageDice(ability, requiredInt(damageFormulaDice(ctx, input, caster, ctx.read.value(extra, 'dice') ?? 0), 'damage_type_roll:hosted-extra-variable-dice-or-mod'));
    const extraDie = requiredInt(ctx.read.value(extra, 'die') ?? 0, 'damage_type_roll:hosted-extra-variable-dice-or-mod');
    const extraMod = fragmentMod(ctx, ctx.read.value(extra, 'mod'), caster);
    const { id: extraType, raw: rawExtraType } = extraPick ? { id: extraPick, raw: extraPick } : extraFragmentType(ctx, extra, caster);
    const extraTypeDefinition = ctx.read.definition(extraType);
    const extraTypeName = String(itemField(extraTypeDefinition, 'item_name') ?? 'Unknown');
    const extraName = itemField(extra, 'item_name');
    const extraSourceType = String(itemField(extra, 'item_type') ?? '');
    const extraMeta: Record<string, unknown> = { damage_type_id: extraType };
    const extraSourceId = itemField(extra, 'item_id');
    if (extraSourceId) extraMeta.source_item_id = extraSourceId;
    if (extraName) extraMeta.source_name = extraName;
    if (extraSourceType) { extraMeta.source_item_type = extraSourceType; extraMeta.source_kind = extraSourceType; }
    const extraStacks = rollTypeSourceStacks(ctx, extra, caster, target,
      upper(ctx.read.value(extra, 'roll_type')) === DOT_ROLL ? DOT_ROLL : DAMAGE_ROLL, upper(extraSourceId));
    const extraRolled = rollDiceFaces(ctx, extraDice, extraDie, extraStacks);
    // Wizard v9 damage_type_is_rolled_at_max on THIS fragment's own type (its dice still draw — the tape stays in step)
    const extraTypeMax = typesAtMax.get(extraType) ?? null;
    // EXPLODING DAMAGE DICE on THIS fragment (damage_type_roll.php ~L553 runs inside each extra's own damage_type_roll()): its kept dice
    // seed chains on the roller's Fortune, drawn right after its dice; a fragment forced to an extreme never explodes (the primary's rule).
    const extraExplode = explodeRule && extraTypeMax === null;
    const extraExploded = extraExplode ? explodeFragmentDice(ctx, extraRolled.faces, extraDie, explodeFortune, extraMeta) : { tuples: [], total: 0 };
    const extraBase = extraTypeMax !== null ? extraDice * extraDie : extraRolled.total + extraExploded.total;
    hostedExtraContributed = true;
    rolledFragmentTypes.push(extraType);
    // if_target_has_status_effect_active/not_active on the HOSTED EXTRA's own definition — the roll still
    // happens (the tape draw must stay in sync with PHP), but check_post_damage_type_roll_conditions.php
    // zeroes this fragment's contribution when the gate fails. Independent gates (PHP runs both blocks
    // unconditionally), so either can zero it.
    // check_post_damage_type_roll_conditions.php (~L480–570): each failing gate marks the fragment's own face tuples
    // (gate_roll_tokens.php damage_roll_fragment_mark_roll_dice_tuples_gate_not_applied — first reason wins) and APPENDS a σ explanation
    // tuple; the fragment then contributes 0. The two gates run independently, required first.
    const fragmentFaces: unknown[][] = [...extraRolled.faces.map(([value, label, meta]) =>
      [value, `${label} | ${extraTypeName}`, { ...meta, ...extraMeta }]), ...extraExploded.tuples as unknown[][]];
    // RIDERS CRIT (owner ruling 2026-10-07; check_critical_hit_logic.php runs for every rider fragment with the HOST's attack roll): on a
    // critical hit this fragment takes the campaign's crit model on its OWN dice exactly as the primary does — Maximum + dice × die,
    // Rolled fresh crit dice, Doubled its result as it stands (mod included). Its tuples follow its dice (and its explosions), before its
    // mod. No cascade d20s (per-attack, first fragment only). Crit: Rolled dice detonate when THIS fragment can explode (PHP passes each
    // fragment's own $fragment_can_explode). The host's multiplier applies to every rider too.
    let extraCrit = 0;
    if (critical) {
      const bonus = criticalHitBonus(ctx.roll, houseRules!.critVariant, { dice: extraDice, die: extraDie, rollResult: extraBase + extraMod },
        { canExplode: extraExplode, fortune: explodeFortune, multiplier: critMultiplier });
      extraCrit = bonus.total;
      if (bonus.tuples.length) for (const tuple of bonus.tuples) fragmentFaces.push([tuple.value, tuple.label, tuple.kind === 'explosion'
        ? { ...extraMeta, kind: 'explosion', explosion_link: tuple.link, explosion_threshold: tuple.threshold, explosion_die: extraDie }
        : { ...extraMeta, kind: 'critical' }]);
      else fragmentFaces.push([bonus.total, bonus.variant === 'doubled' ? 'Critical Damage (Doubled)' : 'Critical Damage', { ...extraMeta, kind: 'critical' }]);
    }
    if (extraMod) fragmentFaces.push([extraMod, String(extraName ?? 'Damage Mod'), { ...extraMeta, kind: 'damage_mod' }]);
    if (objectMultiplier.factor > 1) fragmentFaces.push([`×${objectMultiplier.factor}`,
      `Damage to Objects x${objectMultiplier.factor} — ${objectMultiplier.source}`, extraMeta]);
    // scroll_spell_damage_multiplier: this fragment is part of the scroll-cast spell's damage too (Wizard v9 WP5)
    if (scrollMultiplier.factor > 1) fragmentFaces.push([`×${scrollMultiplier.factor}`, `Scroll Spell Damage x${scrollMultiplier.factor} — ${scrollMultiplier.source}`, extraMeta]);
    // This fragment's OWN typed defences, before check_post's gates (which then stamp these tuples too).
    let extraDefended = defendFragment(ctx, target, rawExtraType, extraTypeName,
      (extraBase + extraMod + extraCrit) * objectMultiplier.factor * scrollMultiplier.factor, fragmentFaces, undefined, casterIgnoresResistance, extra);
    extraDefended = defendSpellFragment(ctx, target, outer, extraMeta, extraDefended, fragmentFaces);
    // this fragment's own contest-half, before its gates (which then mark the '0.5 Λ' tuple too — PHP marks every tuple present)
    if (half) { extraDefended = Math.floor(extraDefended / 2); fragmentFaces.push(['0.5 Λ', 'Half Damage']); }
    const sigma: unknown[][] = [];
    let extraGateBlocked = false;
    const markGate = (reason: string): void => {
      for (const face of fragmentFaces) {
        const meta = (face[2] && typeof face[2] === 'object' ? face[2] : {}) as Record<string, unknown>;
        if (meta.ft_gate_explanation || meta.ft_gate_not_applied) continue;
        face[2] = { ...meta, ft_gate_not_applied: true, ft_gate_reason_code: reason };
      }
    };
    const sigmaMeta = (reason: string, ids: string[]): Record<string, unknown> =>
      ids.length ? { ft_gate_explanation: true, ft_gate_reason_code: reason, ft_gate_status_item_ids: ids, ft_gate_icon_item_ids: ids }
        : { ft_gate_explanation: true, ft_gate_reason_code: reason };
    if (ctx.read.has(extra, 'if_target_has_status_effect_active')) {
      const required = hostedExtraGateIds(ctx, extra, 'if_target_has_status_effect_active');
      const verdicts = required.map((id) => hostedExtraTargetHasStatus(ctx, target, id));
      if (verdicts.includes(null)) handBack('damage_type_roll:hosted-extra-status-gate-not-held');
      if (!verdicts.includes(true)) {
        extraGateBlocked = true;
        markGate('status_required_missing');
        sigma.push(['σ−', 'Target Does Not Have Status Effect Active', sigmaMeta('status_required_missing', [...new Set(required.filter(Boolean))])]);
      }
    }
    if (ctx.read.has(extra, 'if_target_has_status_effect_not_active')) {
      const forbidden = hostedExtraGateIds(ctx, extra, 'if_target_has_status_effect_not_active');
      const verdicts = forbidden.map((id) => hostedExtraTargetHasStatus(ctx, target, id));
      if (verdicts.includes(null)) handBack('damage_type_roll:hosted-extra-status-gate-not-held');
      if (verdicts.includes(true)) {
        extraGateBlocked = true;
        markGate('status_forbidden_present');
        sigma.push(['σ+', 'Target Has Status Effect Active', sigmaMeta('status_forbidden_present', [...new Set(forbidden.filter(Boolean))])]);
      }
    }
    if (!extraGateBlocked) {
      fullResult += Math.max(0, extraDefended);
      if (ctx.read.has(extra, 'damage_heals_caster')) casterHealing += Math.max(0, extraDefended);
    }
    if (!extraGateBlocked && exemptMitigationTypes.has(extraType)) exemptMitigationDamage += Math.max(0, extraDefended);
    fullFaces.push(...fragmentFaces, ...sigma);
  };
  // CASTER extra_damage_roll passives (damage_type_rolls.php ~L174-320), after the primary fragment and before the item-hosted riders, in the
  // caster's passive_ability order (no dedup among them — PHP rolls each entry). The set was settled pre-draw above (casterExtras).
  const rolledExtraDamage = new Set<string>();
  for (const extra of casterExtras) {
    rollExtraFragment(extra.definition, extra.triggered);
    if (!extra.triggered && extra.id) rolledExtraDamage.add(extra.id);
  }
  // damage_type_rolls.php ~L385: untriggered extra_damage_roll riders HOSTED on the ability, after the caster's; one the caster's untriggered
  // loop already rolled (same item id) rolls once.
  // damage_type_rolls.php ~L396–402: the loop runs only when !$skip_extras and the used item's roll_type is neither a weapon melee / ranged
  // attack roll nor a damage-over-time roll (lane A6 — the paoe-entry oracle's DOT entry drew a hosted rider die PHP never draws)
  const hostedRidersSkip = damageOverTime || ['attack_ability', 'opportunity_attack_ability'].includes(String(itemField(ability, 'item_type') ?? ''))
    || ['2FE8DFB7-FE61-4862-AF71-3523262350A0', '3F85CA97-F9B2-A83A-FDE8-E523FD99346C'].includes(abilityRollType);
  for (const hosted of hostedRidersSkip ? [] : ctx.read.entries(ability, 'passive_ability')) {
    const hostedRow = record(hosted);
    if (hostedRow?.passive_conditions_active === false) continue;
    const hostedId = upper(typeof hosted === 'string' ? hosted : hostedRow?.item_id ?? record(hostedRow?.items)?.item_id);
    const extra = record(hostedRow?.item_arr) ?? ctx.read.definition(hostedId);
    if (!extra) hostedPassiveNotHeld(hostedId);
    if (upper(ctx.read.value(extra, 'roll_type')) !== EXTRA_DAMAGE_ROLL) continue;
    if (!extraTypeGateAllows(extra)) continue;
    const extraId = upper(itemField(extra, 'item_id') ?? hostedId);
    if (extraId && rolledExtraDamage.has(extraId)) continue;
    rollExtraFragment(extra, false);
  }
  if (attackAbility) rollExtraFragment(attackAbility, false);
  if (between) {
    const prior = ctx.damagePreparedTarget;
    ctx.damagePreparedTarget = ecId(target);
    const addedBefore = ctx.statusesAdded.length, removedBefore = ctx.statusesRemoved.length;
    // Scope changes to this target's effects: earlier targets' collisions/falls already landed before preparation.
    const touchedOuter = ctx.pushCollisionTouched;
    ctx.pushCollisionTouched = new Set();
    let apply: boolean;
    let touchedHere: Set<string>;
    try { apply = between(); } finally {
      ctx.damagePreparedTarget = prior ?? null;
      touchedHere = ctx.pushCollisionTouched ?? new Set();
      ctx.pushCollisionTouched = new Set([...(touchedOuter ?? []), ...touchedHere]);
    }
    if (!apply) return;
    // Nested damage already killed this recipient; discard its prepared application.
    if (ctx.composedDeaths?.includes(ecId(target))) return;
    // Keep the prepared dice and typed defences; unserved changes to application-time modifiers refuse before real draws.
    applicationStatusGate(ctx, target, addedBefore, removedBefore);
    // Nested collision/fall damage changes vitality. Wake, retry and concentration plans still require a named refusal.
    if (touchedHere.has(ecId(target))) {
      if (wake || charm || retry || concentrating) handBack('damage_type_roll:collision-before-application-state');
      // Apply the prepared hit against the live temp vitality spent by that nested damage.
      tempBefore = requiredInt(record(record(target.computed)?.vitality)?.v_temp ?? ecLean(target, 'v_temp'), 'damage_type_roll:temp-vitality-not-held');
    }
  }
  if (!Number.isSafeInteger(ecDamageReduction) || ecDamageReduction < 0) handBack('damage_type_roll:invalid-ec-dr');
  if (outputPercentage) {
    const adjusted = applyDamagePercentage(fullResult, outputPercentage);
    fullResult = adjusted.total;
    if (adjusted.change) fullFaces.push(['Δ' + adjusted.change, 'All Damage Output Changed by Percentage — Caster Bonus']);
  }
  let result = Math.max(0, fullResult - ecDamageReduction);
  casterHealing = Math.max(0, casterHealing - ecDamageReduction);
  if (ecDamageReduction) fullFaces.push([`-${ecDamageReduction}`, 'Damage Reduction']);
  // Mitigation follows EC-tier reduction; provider decisions were settled before draws.
  if (ctx.read.has(target, 'damage_mitigation')) {
    const raw = scalar(ctx.read.value(target, 'damage_mitigation'));
    const numeric = Number(raw);
    if (raw == null || raw === '' || !Number.isFinite(numeric)) handBack('calc_total_damage_and_apply_to_target:damage-mitigation-unreadable');
    const mitigation = Math.trunc(numeric);
    if (ignoresMitigation) {
      if (numeric > 0) fullFaces.push(['0', 'Damage Mitigation Ignored']); // PHP's log face for an ignored mitigation
    } else if (numeric > 0 && mitigation !== 0) {
      // ignore_damage_mitigation_damage_type (Molten Impact, 2026-10-07 — R-147): exempt-type fragments are not reduced — from what is
      // left once attacks_ignore_natural_damage_mitigation (Living Arsenal, R-180) has skipped the target's natural share
      const subtracted = mitigationAfterTypedIgnore(mitigationAfterNaturalIgnore(mitigation, naturalMitigation), result, exemptMitigationDamage);
      if (subtracted === 0) fullFaces.push(['0', 'Damage Mitigation Ignored']);
      else {
        result = Math.max(0, result - subtracted);
        casterHealing = Math.max(0, casterHealing - subtracted);
        fullFaces.push([`-${subtracted}`, 'Damage Mitigation']);
      }
    } else if (numeric > 0) handBack('calc_total_damage_and_apply_to_target:fractional-damage-mitigation');
  }
  // object_damage_mitigation (props only — target_arr_is_inventory_target): intval(layer_value) > 0, subtracted, floored at 0 (R2 ask 1).
  if (objectTarget && ctx.read.has(target, 'object_damage_mitigation')) {
    const raw = scalar(ctx.read.value(target, 'object_damage_mitigation'));
    const numeric = raw === true ? 1 : Number(raw);
    if (raw == null || raw === '' || !Number.isFinite(numeric)) handBack('calc_total_damage_and_apply_to_target:object-damage-mitigation-unreadable');
    const mitigation = Math.trunc(numeric);
    if (mitigation > 0 && ignoresAllMitigation) fullFaces.push(['0', 'Object Damage Mitigation Ignored']); // Perfect Breach
    else if (mitigation > 0) {
      result = Math.max(0, result - mitigation);
      fullFaces.push([`-${mitigation}`, 'Object Damage Mitigation']);
    }
  }
  const reduced = rollDamageReductions(result, targetDamageReductions(ctx, target), ctx.roll);
  casterHealing = Math.max(0, casterHealing - (result - reduced.total));
  result = reduced.total;
  fullFaces.push(...reduced.adjustments.map(step => [step.face, step.label]));
  consumeShielded(ctx, caster, target);
  // calc_total's roll_feedback (R2 ask 5). A hosted extra fragment's gate / zero reason is not traced → no feedback block (PHP writes
  // one; the row is otherwise exact and the log falls back to the total).
  const feedback: RollFeedback | null = hostedExtraContributed ? null : (() => {
    const applied = Math.max(0, half ? Math.floor(primaryResult / 2) : primaryResult);
    const reasons = primaryFeedbackRolled > 0 ? [applied === 0 ? primaryZero.reason ?? '' : ''] : [];
    const one = new Set(reasons).size === 1;
    return { rolled: primaryFeedbackRolled, applied: result, verb: 'taken' as const,
      reason: result === 0 && one ? reasons[0] || null : null,
      damage_types: [{ damage_type_id: rawPrimaryType.trim(), rolled: primaryFeedbackRolled, applied: applied === result ? applied : null,
        typed: applied, reason: applied === 0 && one ? reasons[0] || null : null }] };
  })();
  surfaceDamageImpact(ctx, ability, caster, target, [...new Set(rolledFragmentTypes)]);
  if (objectTarget && ctx.objectDamage !== undefined) {
    objectDamageApply(ctx, input, ability, caster, target, [...new Set(rolledFragmentTypes)], result, tempBefore, fullFaces, feedback, dice, die, mod, advDisStacks);
    return;
  }
  // OWNER RULING 2026-10-03 (lethal push collisions — lane A11): a creature this use already KILLED (its push collision — as the pushed or the
  // struck creature) takes no further damage from the same cast and is not killed twice: the fragment's dice were drawn (PHP's order — the
  // tape stays aligned), its application, row and second death are not (legacy PHP damaged the corpse and logged a second kill)
  if (ctx.composedDeaths?.includes(ecId(target))) return;
  const targetEc = ec(target);
  const alreadyDead = requiredInt(ecLean(target, 'encounter_creature_dead') ?? ctx.read.deadState(target), 'damage_type_roll:dead-state-not-held');
  if (alreadyDead === 1) return; // A nested on-hit effect may have killed it after preparation.
  const rollType = String(ctx.read.value(ability, 'roll_type') ?? '').toUpperCase() === '77A044B7-7041-CA96-3C63-BB718B15A364'
    ? '77A044B7-7041-CA96-3C63-BB718B15A364' : DAMAGE_ROLL;
  // damage_roll.php reuses its accumulation across an AOE target loop. The first
  // target's type_2 is populated; later targets' accumulated log has an empty type_2.
  // Scoped to THIS janus() dispatch (ctx.dispatchRowStart): an instant volley's projectiles are separate dispatches in PHP.
  const priorDamage = ctx.rows.slice(ctx.dispatchRowStart ?? 0).find((row) => row.roll_item_id === upper(input.abilityId)
    && (row.roll_type_1 === DAMAGE_ROLL || row.roll_type_1 === '77A044B7-7041-CA96-3C63-BB718B15A364'));
  const type2 = priorDamage && priorDamage.roll_to !== ecId(target) ? '' : types[0];
  insertRoll(ctx, { roll_type_1: rollType, roll_type_2: type2, roll_item_id: input.abilityId,
    by: caster, to: target, roll_result: result, rolls_full: fullFaces, dice, die, mod, adv_dis_stacks: advDisStacks, feedback });
  if (result > 0) queueNextSpellTarget(ability, target);
  // calc_total_damage_and_apply_to_target logs first, then apply_damage_to_target absorbs/reverts before overflow.
  absorbTemporaryVitality(ctx, target, result, tempBefore);
  const before = requiredInt(ecLean(target, 'v_current'), 'damage_type_roll:vitality-not-held');
  const applied = Math.max(0, result - tempBefore);
  let lethal = before <= applied;
  targetEc.v_current = lethal ? 0 : before - applied;
  patchVitalityCluster(target, targetEc.v_current as number);
  fireDamageContingencies(ctx, target, before);
  // A contingency may restore vitality before the zero-vitality/death branch.
  lethal = requiredInt(ecLean(target, 'v_current'), 'damage_type_roll:vitality-not-held') <= 0;
  // MAIMED ON CRIT (apply_damage_to_target_inc.php ~L227–286; lane A8, 2026-10-02 — the 09-30 playtest campaign runs it, so every attack there
  // handed back): after the damage row, before the vitality lands, a critical hit under the campaign's Maimed rule makes the TARGET roll a DC 15
  // Fortitude save (skill_roll_arr, is_target, the rule's banner) and gain Maimed on a failure. Lane A7's port was pulled because the save reads
  // the roller's own is_target `roll_type_against_*` sources (a held Evade → DIS) — modelled now (skill-roll.ts rollTypeModifierStacks).
  const maimedBefore = damageMaimedStacks(ctx, target);
  if (critical && houseRules?.maimed) maimedOnCrit(ctx, input, ability, caster, target, weaponHit);
  // apply_damage_to_target.php's death branch runs AFTER the damage_roll row, BEFORE the post-damage
  // trigger fan-out (damageTakenReactions below, which already covers only the reaction-ability slice
  // of that fan-out for the surviving case too — unchanged here). A PC left DYING (not yet truly dead)
  // still gets it — a real capture proved Violent Focus fires there (Fleshdobbin Shriek) — but a
  // CREATURE this same call just tail-killed does not (a real capture — Cobra Spray — showed PHP never
  // dispatches it once encounter_creature_dead=1 lands: a corpse holds no reaction to spend).
  if (lethal) resolvePlayerDying(ctx, input, ability, caster, target, applied, critical);
  // A CREATURE kill silences its damage-taken reactions (Cobra Spray capture); a player character dying to MASSIVE damage
  // still fires them after the death rows (Wall of Ice / Wall of Thorns self-captures: Violent Focus's no_roll + its charge).
  const deathComposed = ctx.composedDeaths?.includes(ecId(target)) ?? false;
  const justKilled = deathComposed && String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '') !== 'player_character';
  // R-113 (apply_damage_to_target "IF SLEEPING WE WAKE UP"): vitality damage landed → the sleeper wakes, before the damage-taken fan-out
  if (wake && applied > 0) performWake(ctx, wake, caster, target, lethal);
  // the holder survived → a Charmed / Dominated row re-rolls its contest (PHP: after the wake and reveal, before R-150)
  if (charm && applied > 0) performCharmBreak(ctx, input, charm, target);
  // R-150: the holder survived → it re-rolls the flagged status's contest (PHP: after the charm break, before the damage-taken fan-out)
  if (retry && applied > 0 && !lethal) performContestRetry(ctx, input, retry, target);
  if (applied > 0) damageTakenTriggers(ctx, input, caster, target, [...new Set(rolledFragmentTypes.map(upper))], justKilled, critical);
  // check_concentration.php runs last in apply_damage_to_target: total_damage_to_target > 0 (the summary total, before
  // temporary vitality absorbs it) on a Concentrating target → Psyche DC max(10, floor(damage / 2)).
  if (concentrating && result > 0 && !deathComposed) finishDamageConcentration(ctx, input, target, concentrating, result, justKilled,
    { maimedBefore });
  if (!objectTarget) damageHealsCaster(ctx, input, ability, caster, casterHealing);
}

/** calc_total_damage_and_apply_to_target's reads of the target's STATUSES (the prepared-path gate: a row the effects added / removed between
 *  the fragment's preparation and its application). Mirrors the status loop above that the page settles before the effects. */
const APPLICATION_STATUS_KEYS = ['damage_mitigation', 'transfer_damage_percentage_to_caster_of_status_effect', 'all_incoming_damage_changed_by_percentage',
  'damage_reduction', 'damage_taken_pct_mod', 'reduce_damage_roll', 'warding_bond', 'damage_redirect', 'object_damage_mitigation',
  'triggers_when_target_takes_damage', 'triggers_when_caster_takes_any_damage', 'triggers_when_caster_takes_damage_type', 'charmed',
  'triggers_on_self_zero_vitality', 'transform_into_creature_on_self_zero_vitality', 'passive_ability',
  // the wake plan (planWake, settled before the effects): a sleep LOCK the effects add / remove (Codex A5 review 2 P2)
  'sleeping_not_ended_by_damage',
  // …and a Sleeping-LOSS trigger carrier (the wake plan's lossArm — PHP dispatches it on the CURRENT target at the removal; Codex A5 review 3 P2)
  'triggers_when_caster_loses_status_effect',
  // the contest-retry plan (planContestRetry, settled before the effects): a flagged status the effects add / remove (R-150, 2026-10-07)
  'target_retries_contesting_roll_on_damage_taken'];
// + SLEEPING: a sleeper the effects put to sleep / wake is not in the wake plan settled before them (Codex A5 review 2 P2)
// + Dominated: the charm-break plan is settled before the effects too (Charmed is caught by its name below)
const APPLICATION_STATUS_IDS = new Set([CONCENTRATING, DYING_ID, SLEEPING, 'DC9778C6-5B9B-1501-B86A-5EEF4671F66A', '3BDE1AA4-23D6-C4E8-CADD-ED15B4403EA6',
  'D2E7BA2C-562D-D4AC-7F71-13E4AC540484']);
function applicationStatusGate(ctx: JanusCtx, target: Row, addedBefore: number, removedBefore: number): void {
  const id = ecId(target);
  const touched = [...ctx.statusesAdded.slice(addedBefore), ...ctx.statusesRemoved.slice(removedBefore)].filter((s) => upper(s.target) === id);
  for (const status of touched) {
    const statusId = upper(status.status_item_id);
    if (APPLICATION_STATUS_IDS.has(statusId) || DAMAGE_BRANCH_TARGET_STATUS_IDS.has(statusId)) handBack('damage_type_roll:effect-status-before-application:' + statusId);
    const definition = ctx.read.definition(statusId) ?? record((status as unknown as Row).status_arr);
    if (!definition) definitionNotHeld('damage_type_roll:effect-status-before-application:definition-not-held', statusId);
    if (APPLICATION_STATUS_KEYS.some((key) => ctx.read.has(definition!, key))
        || String(record(definition!.items)?.item_name ?? '').toLowerCase() === 'charmed')
      handBack('damage_type_roll:effect-status-before-application');
  }
  targetDamageReductions(ctx, target);
}

/** The target's current payload statuses, including this hit's pre-damage defensive reaction. The status is never consumed here. */
export function targetDamageReductions(ctx: JanusCtx, target: Row): DamageReduction[] {
  const reductions: DamageReduction[] = [];
  for (const status of activeStatuses(ctx, target)) {
    const id = statusItemId(status);
    const definition = pendingStatusDefinitions.get(status) ?? ctx.read.definition(id) ?? record(status.status_arr);
    if (!definition) definitionNotHeld('calc_total_damage_and_apply_to_target:unmodelled:status-definition', id);
    const reduction = damageReductionPlan(definition!, (row, key) => ctx.read.value(row, key));
    if (reduction === 'invalid') handBack('reduce-damage-status-dice-out-of-scope');
    if (reduction) reductions.push(reduction);
  }
  return reductions;
}

/** A carrier (row itself, or one of its resolved passive_ability entries) authors any of `keys`.
 * An unresolvable passive definition hands back rather than silently skip a possible match. */
/** maybe_last_stand_heal_on_zero_vitality / maybe_transform_creature_on_zero_vitality: a self-zero ward the target holds on its row, an
 * active passive or a held status (the Death Ward EFFECT status — apply_death_effect.php's own example). Read only once a hit takes the
 * target to zero vitality, so a ward-holder hit for less resolves; reaching zero is not modelled (named hand-back at the call sites). */
function zeroVitalityWardHeld(ctx: JanusCtx, target: Row): boolean {
  const keys = ['triggers_on_self_zero_vitality', 'transform_into_creature_on_self_zero_vitality'];
  if (rowOrPassivesHasAny(ctx, target, keys, 'calc_total_damage_and_apply_to_target:target-passive-definition-not-held')) return true;
  return activeStatuses(ctx, target).some((status) => {
    const definition = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!definition) handBack('calc_total_damage_and_apply_to_target:unmodelled:status-definition');
    return keys.some((key) => ctx.read.has(definition, key));
  });
}
function rowOrPassivesHasAny(ctx: JanusCtx, row: Row, keys: string[], missingDefinitionReason: string): boolean {
  if (keys.some((key) => ctx.read.has(row, key))) return true;
  const resolved = record(record(record(row.computed)?.creature)?.abilities)?.passive_ability;
  for (const passive of [...ctx.read.entries(row, 'passive_ability'), ...(Array.isArray(resolved) ? resolved : [])]) {
    const definition = carrierDefinition(ctx, passive, missingDefinitionReason);
    if (!definition) continue;
    if (keys.some((key) => ctx.read.has(definition, key))) return true;
  }
  return false;
}

/** Resolve a non-PC death after the actual damage and compose its rows before on-kill effects. */
function resolveCreatureDeath(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, applied: number): void {
  if (resolveKnockout(ctx, input, ability, caster, target)) return;
  if (ctx.dryRun) {
    if (!ctx.read.deathResources) handBack('death-tail:corpse-row-plan-required');
    composeActualDeath(ctx, caster, target, ability, applied);
    return;
  }
  if (ctx.spawnTurnTransition?.next_id === ecId(target)) handBack('spawn:transform-successor-dies');
  if (ctx.read.spawnTransform) {
    if (!(String(itemField(ability, 'item_type') ?? '') === 'attack_ability' && ctx.read.has(ability, 'non_lethal_attack'))
      && resolveSelfZeroTransform(ctx, target)) return;
  }
  deathPathGates(ctx, ability, caster, target);
  composeActualDeath(ctx, caster, target, ability, applied);
  onKillTriggers(ctx, caster, target);
}

/** Native knockout: permanent Prone, ten-minute Unconscious and held drops; no corpse. */
function resolveKnockout(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row): boolean {
  if (String(itemField(ability, 'item_type') ?? '') !== 'attack_ability' || !ctx.read.has(ability, 'non_lethal_attack')) return false;
  if (ctx.dryRun) return true;
  deathResourcesFor(ctx, target);
  applyStatusEffect(ctx, input, ability, PRONE_ID, 'status_effect_applied', caster, target,
    { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] });
  const duration = getStatusEffectDurationTimes(ctx, { item_layer_types: { duration_int: 10, duration_type: 'minutes' } }, target);
  applyStatusEffect(ctx, input, ability, 'F73BA661-ADD2-26E0-E265-FBEB7E5E6AF1', 'status_effect_applied', caster, target, duration);
  dropDyingHands(ctx, target);
  grantControllerMenace(ctx, caster, target);
  onKillTriggers(ctx, caster, target);
  return true;
}

/** Everything that would change WHETHER / HOW a creature dies, or write more in this same use after its death — shared by
 * the creature kill and the player character's massive-damage classification. */
function deathPathGates(ctx: JanusCtx, ability: Row, caster: Row, target: Row): void {
  if (rowOrPassivesHasAny(ctx, target, [...(ctx.read.spawnTransform ? [] : ['transform_into_creature_on_self_zero_vitality'])],
      'calc_total_damage_and_apply_to_target:target-passive-definition-not-held'))
    skipDeathDuty(ctx, target, 'Self transformation at zero vitality is not yet supported.');
  if (rowOrPassivesHasAny(ctx, caster, ['transform_killed_target_into_controlled_creature'],
      'death:create-spawn-provider-not-held'))
    refuseDeath(ctx, caster, target, ability, 'Create Spawn cannot yet resolve a killing blow. Ask your GM to resolve this kill.');
  onKillGate(ctx, caster, target, true);
}

/** An explicit Dead effect skips PC Dying, but shares zero-vitality wards and the one actual-death frame. */
export function resolveInstantDeath(ctx: JanusCtx, ability: Row, caster: Row, target: Row): boolean {
  if (ctx.composedDeaths?.includes(ecId(target)) || Number(ecLean(target, 'encounter_creature_dead') ?? ctx.read.deadState(target)) === 1) return false;
  ec(target).v_current = 0;
  patchVitalityCluster(target, 0);
  if (tryLastStand(ctx, target, caster)) return false;
  deathPathGates(ctx, ability, caster, target);
  composeActualDeath(ctx, caster, target, ability, 0);
  if (!ctx.dryRun) onKillTriggers(ctx, caster, target);
  return true;
}

/** PC Dying entry: attacker Prone loss, permanent statuses, held drops and supported on-kill hooks. */
export function resolvePlayerDying(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, applied: number, critical = false): void {
  if (ctx.composedDeaths?.includes(ecId(target))) return;
  if (tryLastStand(ctx, target, caster)) return;
  // itemField() alone reads only a top-level `items` bucket; a PC row here (unlike a definition row) commonly
  // carries its type ONLY under `encounter_creatures.item_type` (real capture data, no `items` bucket at all) —
  // the SAME fallback ability-use.ts's own target-kind check already uses (`itemField ?? ecLean`). Without it
  // this dispatch misreads every such PC row as "not a player character" and wrongly takes the creature branch,
  // which is exactly the divergence a real Disintegrate/Fire Bombard capture exposed (rows-differ once the
  // creature branch stopped being an unconditional hand-back).
  if (String(itemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '') !== 'player_character') {
    resolveCreatureDeath(ctx, input, ability, caster, target, applied);
    return;
  }
  const vitality = record(record(target.computed)?.vitality);
  const vMax = requiredInt(vitality?.v_max ?? ecLean(target, 'v_max'), 'calc_total_damage_and_apply_to_target:v-max-not-held');
  // apply_damage_to_target_inc.php's zero-vitality chain tests MASSIVE damage on a player character BEFORE the knockout
  // strike and the dying branches: kill_creature → apply_death_effect → the massive_damage_instant_death row. The browser
  // composes that same tail at this point in the action's row plan.
  if (applied >= vMax) {
    if (ctx.dryRun) {
      if (!ctx.read.deathResources) handBack('death-tail:corpse-row-plan-required');
      composeActualDeath(ctx, caster, target, ability, applied);
      return;
    }
    deathPathGates(ctx, ability, caster, target);
    composeActualDeath(ctx, caster, target, ability, applied);
    onKillTriggers(ctx, caster, target);
    return;
  }
  if (resolveKnockout(ctx, input, ability, caster, target)) return;
  if (activeStatuses(ctx, target).some(status => statusItemId(status) === DYING_ID)) {
    if (!ctx.dryRun) {
      deathResourcesFor(ctx, target);
      resolveDyingStacks(ctx, input, ability, caster, target, critical);
      grantControllerMenace(ctx, caster, target);
      onKillTriggers(ctx, caster, target);
    }
    return;
  }
  // maybe_last_stand_heal_on_zero_vitality / maybe_transform_creature_on_zero_vitality: any creature —
  // player, NPC, or summon — can hold a triggers_on_self_zero_vitality / transform_into_creature_on_self_zero_vitality
  // reaction (directly authored, passive-granted, or status-hosted — the target-status loop above already
  // covers the status-hosted case). triggers_on_kill is the CASTER-side mirror (janus_trigger fires it on
  // every kill/dying transition, not only a true kill).
  if (rowOrPassivesHasAny(ctx, target, ['transform_into_creature_on_self_zero_vitality'],
      'calc_total_damage_and_apply_to_target:target-passive-definition-not-held'))
    skipDeathDuty(ctx, target, 'Self transformation at zero vitality is not yet supported.');
  // janus_trigger()'s full carrier walk (every carrier bucket + live statuses' status_arr) for the killer's triggers_on_kill.
  onKillGate(ctx, caster, target);
  // Native apply_player_dying removes the ATTACKER's Prone, including its single-connection
  // peer/lone-source and loss-hook consequences, before adding the victim's three statuses.
  if (!ctx.dryRun && ctx.read.deathResources) deathResourcesFor(ctx, target);
  for (const prone of [...activeStatuses(ctx, caster)].filter(status => statusItemId(status) === PRONE_ID))
    removeStoredStatus(ctx, caster, prone, 'single');
  // apply_player_dying.php passes a literal `status_ends: 0` to all three of its own calls — permanent,
  // never the casting ability's own duration (a real capture exposed this: Wall of Light's timed wall
  // duration was leaking onto its victim's Dying stack).
  const PERMANENT: Parameters<typeof applyStatusEffect>[7] = { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] };
  applyStatusEffect(ctx, input, ability, CRIPPLED_ID, 'status_effect_applied', caster, target, PERMANENT);
  applyStatusEffect(ctx, input, ability, DYING_ID, 'status_effect_applied', caster, target, PERMANENT);
  if (activeStatuses(ctx, target).some(status => statusItemId(status) === MAIMED_ID
      && readStatusRowStack(status) >= QUANTITY_FAMILIES[MAIMED_ID]!.max)) {
    applyStatusEffect(ctx, input, ability, DEAD_STATUS_ID, 'status_effect_applied', caster, target, PERMANENT, true);
    return;
  }
  applyStatusEffect(ctx, input, ability, MAIMED_ID, 'status_effect_applied', caster, target, PERMANENT, true);
  dropDyingHands(ctx, target);
  grantControllerMenace(ctx, caster, target);
  const landing = deathLanding(ctx, target);
  Object.assign(ec(target), landing.changes);
  if (landing.changes.z_pos !== undefined) target.position = { ...record(target.position), z_pos: landing.changes.z_pos };
  if (!ctx.dryRun && landing.fallFt > 0.05) {
    const falling = ctx.read.definition(FALLING_DAMAGE_ID);
    if (!falling) definitionNotHeld('death:fall-damage-definition-not-held', FALLING_DAMAGE_ID);
    resolveFallDamage(ctx, input, falling, target, landing.fallFt);
  }
  onKillTriggers(ctx, caster, target);
}

/**
 * calc_total_damage_and_apply_to_target → apply_damage_to_target() on an OBJECT (target_arr_is_inventory_target; R2 asks 3–4): the
 * damage row (roll_by / roll_to = the inventory ids), then v_temp absorbs, adjust_v_current floors at 0 and clamps to the effective
 * max, ft_inventory_forced_destruction_by_damage_type floors it when damage still landed after v_temp and one of the roll's types is
 * listed in destroyed_by_damage_type, and v_current 0 → destroyed (the caller runs handle_inventory_destroyed_from_damage's twin). A
 * destroyed object skips the creature tail; a surviving one reaches the damage fan-out, which the pre-draw gates already cleared (no
 * by-id status, no takes-damage carrier), and check_concentration (an object never concentrates — a Concentrating row hands back).
 */
function objectDamageApply(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, target: Row, types: string[], result: number,
  tempBefore: number, fullFaces: unknown[], feedback: RollFeedback | null, dice: number, die: number, mod: number, advDisStacks: number): void {
  const sections = result > 0 ? wallRunDamageRedirect(ctx, caster, target, input.abilityId, ctx.read.has(ability, 'aoe_type')) : null;
  if (sections !== null) {
    for (const section of sections) objectDamageApply(ctx, input, ability, caster, section, types, result,
      Number(record(record(section.computed)?.vitality)?.v_temp ?? 0), fullFaces, feedback, dice, die, mod, advDisStacks);
    return;
  }
  if (ctx.read.statuses(target).some((status) => upper(status.status_item_id) === CONCENTRATING))
    handBack('check_concentration:object-concentrating');
  const vitality = record(record(target.computed)?.vitality)!;
  const before = requiredInt(vitality.v_current, 'apply_damage_to_target:object-v-current-not-held');
  const effectiveMax = requiredInt(vitality.effective_v_max ?? vitality.v_max, 'apply_damage_to_target:object-v-max-not-held');
  const rollType = upper(ctx.read.value(ability, 'roll_type')) === DOT_ROLL ? DOT_ROLL : DAMAGE_ROLL;
  const priorDamage = ctx.rows.slice(ctx.dispatchRowStart ?? 0).find((row) => row.roll_item_id === upper(input.abilityId) && (row.roll_type_1 === DAMAGE_ROLL || row.roll_type_1 === DOT_ROLL));
  const type2 = priorDamage && priorDamage.roll_to !== inventoryId(target) ? '' : types[0]!;
  insertRoll(ctx, { roll_type_1: rollType, roll_type_2: type2, roll_item_id: input.abilityId,
    by: caster, to: target, roll_result: result, rolls_full: fullFaces, dice, die, mod, adv_dis_stacks: advDisStacks, feedback });
  let damage = Math.max(0, result);
  let vTemp = Math.max(0, tempBefore);
  if (vTemp > 0) {
    if (vTemp >= damage) { vTemp -= damage; damage = 0; } else { damage -= vTemp; vTemp = 0; }
  }
  let vCurrent = Math.max(0, Math.min(effectiveMax, before - damage));
  const destroyedBy = new Set(ctx.read.entries(target, 'destroyed_by_damage_type').map((entry) => upper(listEntryUuid(entry))).filter(Boolean));
  const forced = damage > 0 && vCurrent > 0 && types.some((id) => destroyedBy.has(upper(id)));
  if (forced) vCurrent = 0;
  vitality.v_current = vCurrent;
  if (vTemp > 0) vitality.v_temp = vTemp; else delete vitality.v_temp;
  const bucket = record(target.inventory);
  if (bucket) { bucket.v_current = vCurrent; bucket.v_temp = vTemp; }
  const hit = { inventory_id: inventoryId(target), before: { v_current: before, v_temp: tempBefore }, v_current: vCurrent, v_temp: vTemp,
    destroyed: vCurrent <= 0, forced, damageTypeIds: [...types], total: result, afterRow: ctx.rows.length };
  ctx.objectDamage!.push(hit);
  if (ctx.saveObjectDamage) saveAbilityObjectDamage(ctx, caster, target, hit);
}
